<template>
  <div class="analytics-shell">
    <header class="analytics-header">
      <div class="header-copy">
        <p class="eyebrow">Analytics</p>
        <h1>{{ analyticsHeading }}</h1>
        <p v-if="focusCampaign" class="header-subtle">
          {{ focusCampaign.lifecycleStatus }} campaign
          <span v-if="focusCampaign.startDate || focusCampaign.endDate">
            · {{ campaignScheduleLabel }}
          </span>
        </p>
      </div>

      <div class="header-actions">
        <button type="button" class="ghost-button" @click="goDashboard">
          Dashboard
        </button>
      </div>
    </header>

    <section v-if="isLoading" class="surface loading-state">
      Loading analytics…
    </section>

    <section v-else-if="loadError" class="surface warning-state">
      <h2>Unable to load analytics</h2>
      <p>{{ loadError }}</p>
      <button type="button" class="ghost-button" @click="refreshAnalytics">
        Try again
      </button>
    </section>

    <section v-else-if="!campaigns.length" class="surface empty-state">
      <h2>No campaigns found</h2>
      <p>Analytics appear here once your tenant has campaigns to measure.</p>
    </section>

    <template v-else>
      <article class="surface feature-card analytics-portfolio-card">
        <header class="panel-header panel-header--with-actions">
          <div>
            <h2>Your Campaigns</h2>
            <p>Campaign-first ranking across the tenant.</p>
          </div>
          <div class="panel-header-actions">
            <span class="panel-header-meta">{{ lastUpdatedLabel }}</span>
            <button type="button" class="ghost-button ghost-button--small" @click="refreshAnalytics">
              Refresh
            </button>
          </div>
        </header>

        <div class="portfolio-table">
          <div class="portfolio-row portfolio-row--head">
            <span>Campaign</span>
            <span>Status</span>
            <span>Registrations</span>
            <span>Gameplays</span>
          </div>
          <div v-if="focusCampaign" class="portfolio-row">
            <label class="row-link-select-shell" for="campaign-focus-select">
              <span class="sr-only">Choose campaign</span>
              <select
                id="campaign-focus-select"
                class="row-link-select"
                :value="focusCampaign.id"
                @change="focusOnCampaign(($event.target as HTMLSelectElement).value)"
              >
                <option
                  v-for="campaign in rankedCampaigns"
                  :key="`option-${campaign.id}`"
                  :value="campaign.id"
                >
                  {{ formatCampaignSelectLabel(campaign.name) }}
                </option>
              </select>
            </label>
            <span class="portfolio-cell" data-label="Status">
              <span class="portfolio-status-badge" :class="`portfolio-status-badge--${focusCampaign.lifecycleStatus.toLowerCase()}`">
                {{ focusCampaign.lifecycleStatus }}
              </span>
            </span>
            <span class="portfolio-cell" data-label="Registrations">{{ formatNumber(focusBoardMetrics.registered) }}</span>
            <span class="portfolio-cell" data-label="Gameplays">{{ formatNumber(focusBoardMetrics.gameplays) }}</span>
          </div>
        </div>
      </article>

      <section v-if="focusCampaign" class="surface analytics-over-time">
        <header class="panel-header">
          <div>
            <h2>Campaign analytics</h2>
            <p>Registrations, Gameplays, and Prizes Won over time. Select a window to refresh.</p>
          </div>
          <AnalyticsRangePicker v-model="analyticsRange" :data-span="dataSpan" />
        </header>

        <div class="over-time-totals">
          <article class="metric-card">
            <span class="metric-label">Total Registrations</span>
            <strong class="metric-value">{{ formatNumber(overTimeTotals.registrations) }}</strong>
            <span class="metric-detail">{{ analyticsRangeLabel }}</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Total Gameplays</span>
            <strong class="metric-value">{{ formatNumber(overTimeTotals.gameplays) }}</strong>
            <span class="metric-detail">{{ analyticsRangeLabel }}</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Total Prizes Won</span>
            <strong class="metric-value">{{ formatNumber(overTimeTotals.prizesWon) }}</strong>
            <span class="metric-detail">{{ analyticsRangeLabel }}</span>
          </article>
        </div>

        <p v-if="overTimeLoading" class="muted-message">Loading charts…</p>
        <p v-else-if="overTimeError" class="muted-message muted-message--warn">{{ overTimeError }}</p>
        <div v-else class="over-time-charts">
          <article class="chart-card">
            <h3>Registrations over time</h3>
            <div class="chart-canvas-wrap">
              <Line :data="overTimeRegistrationsChart" :options="chartCommonOptions" />
            </div>
          </article>
          <article class="chart-card">
            <h3>Gameplays over time</h3>
            <div class="chart-canvas-wrap">
              <Line :data="overTimeGameplaysChart" :options="chartCommonOptions" />
            </div>
          </article>
          <article class="chart-card">
            <h3>Prizes Won over time</h3>
            <div class="chart-canvas-wrap">
              <Line :data="overTimePrizesAllocatedChart" :options="chartCommonOptions" />
            </div>
          </article>
        </div>
      </section>

      <details class="advanced-charts-drawer" open>
        <summary>Advanced activity charts — {{ advancedChartsSummary }}</summary>
        <section class="surface analytics-charts">
        <header class="panel-header">
          <div>
            <h2>Activity charts</h2>
            <p>Same time window as above. CSV export covers the full record.</p>
          </div>
        </header>

        <p v-if="chartLoading" class="muted-message">Loading charts…</p>
        <p v-else-if="chartError" class="muted-message muted-message--warn">{{ chartError }}</p>
        <p v-else-if="!chartSeries?.series?.length" class="muted-message">No activity in this window yet.</p>
        <div v-else class="charts-grid">
          <article class="chart-card">
            <h3>Funnel totals</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="funnelChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card">
            <h3>Gameplays per registrant</h3>
            <div class="chart-canvas-wrap">
              <Line :data="conversionChart" :options="chartCommonOptions" />
            </div>
          </article>
          <article class="chart-card">
            <h3>Conversion rate</h3>
            <div class="chart-canvas-wrap">
              <Line :data="conversionRateChart" :options="percentageChartOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'quiz'">
            <h3>Quiz drop-off</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="quizDropOffChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'quiz'">
            <h3>Quiz leaderboard</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="quizLeaderboardChart" :options="quizLeaderboardChartOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'survey'">
            <h3>Responses per question</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="quizDropOffChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'survey'">
            <h3>Top answer per question</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="surveyTopAnswerChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'ar'">
            <h3>AR engagement funnel</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="arFunnelChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="campaignKind === 'ar'">
            <h3>Session duration</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="arDurationChart" :options="horizontalBarOptions" />
            </div>
          </article>
          <article class="chart-card" v-if="showGeoSurfaces">
            <h3>Geolocation</h3>
            <div class="chart-canvas-wrap">
              <Bar :data="geoPointsChart" :options="chartCommonOptions" />
            </div>
          </article>
        </div>
      </section>
      </details>

      <section class="surface analytics-download">
        <div class="download-header">
          <div>
            <h2>Download user-level CSV</h2>
            <p>
              One row per user with their full action summary (logins, plays, claims, quiz completions).
              Each $50 expansion pack unlocks up to 1,000 users for this campaign.
            </p>
          </div>
          <div class="download-cta">
            <button
              type="button"
              class="download-button"
              :disabled="downloadBusy || !focusCampaign"
              @click="onDownloadClick"
            >
              <span v-if="downloadBusy">Working…</span>
              <span v-else-if="!downloadEntitlement?.canPurchase">Unlock downloads</span>
              <span v-else-if="!downloadEntitlement?.purchased">Buy download pack</span>
              <span v-else-if="entitlementCoversAll">Download CSV</span>
              <span v-else>Download options</span>
            </button>
            <p v-if="downloadEntitlement?.purchased" class="download-meta">
              Packs owned: {{ downloadEntitlement.packsOwned }} · Users covered: {{ downloadEntitlement.userCapTotal }}
            </p>
          </div>
        </div>
        <label
          v-if="campaignKind === 'quiz' || campaignKind === 'survey'"
          class="download-option"
        >
          <input type="checkbox" v-model="includeAnswersInCsv" />
          <span>Include per-user answers (extra column per question)</span>
        </label>
        <p v-if="downloadError" class="download-error">{{ downloadError }}</p>
      </section>

      <teleport to="body">
        <div v-if="paywallModal" class="paywall-overlay" @click.self="paywallModal = null">
          <div class="paywall-card">
            <button type="button" class="paywall-close" @click="paywallModal = null" aria-label="Close">&times;</button>

            <template v-if="paywallModal === 'confirm-purchase'">
              <h3 v-if="!downloadEntitlement?.canPurchase">Subscribe and unlock downloads</h3>
              <h3 v-else>Unlock downloads for this campaign</h3>

              <p v-if="!downloadEntitlement?.canPurchase">
                Analytics CSV export needs the Pro subscription (<strong>$25/mo</strong>) plus a
                one-time expansion pack per campaign (<strong>$50</strong> per 1,000 users).
                We'll put both into one Stripe checkout — subscribe and unlock in a single step.
              </p>
              <p v-else-if="uniqueActorsInChart > 1000">
                This campaign has {{ uniqueActorsInChart }} unique users in your selected window.
                Covering all of them needs {{ packsNeededForAll }} packs at $50 each — total
                <strong>${{ packsNeededForAll * 50 }}</strong>.
              </p>
              <p v-else>
                Unlock CSV export for this campaign — up to 1,000 users for
                <strong>$50</strong>. Downloads are unlimited once unlocked.
              </p>

              <div class="paywall-quantity">
                <label>Packs to buy now</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  :value="paywallExtraPacks"
                  @input="paywallExtraPacks = Math.max(1, Math.min(50, Number(($event.target as HTMLInputElement).value) || 1))"
                />
                <span>
                  = ${{ paywallExtraPacks * 50 }}<span v-if="!downloadEntitlement?.canPurchase"> + $25/mo</span>
                </span>
              </div>

              <div class="paywall-actions">
                <button type="button" class="paywall-secondary" @click="paywallModal = null">Cancel</button>
                <button type="button" class="paywall-primary" :disabled="downloadBusy" @click="confirmPurchasePack(paywallExtraPacks)">
                  {{ downloadBusy ? 'Redirecting…' : 'Continue to Stripe' }}
                </button>
              </div>
            </template>

            <template v-else-if="paywallModal === 'overflow'">
              <h3>Campaign has more users than your entitlement</h3>
              <p>
                This campaign has {{ uniqueActorsInChart }} unique users in your selected window,
                but your current entitlement covers {{ downloadEntitlement?.userCapTotal || 0 }}.
              </p>
              <p>Two options:</p>
              <div class="paywall-actions paywall-actions--stacked">
                <button
                  type="button"
                  class="paywall-secondary"
                  :disabled="downloadBusy"
                  @click="startCsvDownload('latest')"
                >
                  {{ downloadBusy ? 'Working…' : `Download latest ${downloadEntitlement?.userCapTotal || 0} users` }}
                </button>
                <button
                  type="button"
                  class="paywall-primary"
                  :disabled="downloadBusy"
                  @click="confirmPurchasePack(paywallExtraPacks)"
                >
                  Buy {{ paywallExtraPacks }} more pack(s) for ${{ paywallExtraPacks * 50 }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </teleport>

      <section
        v-if="campaignKind !== 'other' || (showGeoSurfaces && campaignHasGeoPoints)"
        class="surface analytics-type-insights"
        aria-label="Campaign-specific insights"
      >
        <header class="type-insights-header">
          <div>
            <h2>Campaign insights</h2>
            <p>
              Detected experience: <strong>{{ focusCampaign?.experienceType || 'unknown' }}</strong>.
              Sections below are specific to this campaign type and load on demand.
            </p>
          </div>
          <span class="type-badge" :data-kind="campaignKind">{{ campaignKind }}</span>
        </header>

        <details
          v-if="showGeoSurfaces && campaignHasGeoPoints"
          class="type-tile"
          :open="geoSectionOpen"
          @toggle="geoSectionOpen = ($event.target as HTMLDetailsElement).open"
        >
          <summary>
            <span class="type-tile-title">Where people engaged</span>
            <span class="type-tile-hint">{{ geoSectionOpen ? 'Hide map' : 'Show map' }}</span>
          </summary>
          <div class="type-tile-body">
            <p class="muted-message">Map view of claim/engagement locations — coming in the next update. {{ chartSeries?.totals?.geoPoints ?? 0 }} geo-tagged events in the current window.</p>
          </div>
        </details>

        <details
          v-if="campaignKind === 'quiz' || campaignKind === 'survey'"
          class="type-tile"
          :open="campaignKind === 'quiz' ? quizSectionOpen : surveySectionOpen"
          @toggle="campaignKind === 'quiz'
            ? (quizSectionOpen = ($event.target as HTMLDetailsElement).open)
            : (surveySectionOpen = ($event.target as HTMLDetailsElement).open)"
        >
          <summary>
            <span class="type-tile-title">
              {{ campaignKind === 'quiz' ? 'Quiz answers — top choices' : 'Survey answers — top choices' }}
            </span>
            <span class="type-tile-hint">
              {{ (campaignKind === 'quiz' ? quizSectionOpen : surveySectionOpen) ? 'Hide' : 'Show' }}
            </span>
          </summary>
          <div class="type-tile-body">
            <p v-if="answerSummaryLoading" class="muted-message">Loading answer summary…</p>
            <p v-else-if="answerSummaryError" class="muted-message muted-message--warn">{{ answerSummaryError }}</p>
            <template v-else-if="answerSummary">
              <div class="answer-summary-meta">
                <span>
                  <strong>{{ answerSummary.totalRespondents }}</strong>
                  {{ answerSummary.totalRespondents === 1 ? 'respondent' : 'respondents' }}
                </span>
                <span v-if="answerSummary.truncated" class="answer-summary-truncated">
                  Showing the first 2,000 sessions — buy the download pack for the full dataset.
                </span>
              </div>
              <div v-if="!answerSummary.questions.length" class="muted-message">
                No completed sessions with answers in the current window.
              </div>
              <div v-else class="answer-summary-list">
                <article
                  v-for="q in answerSummary.questions"
                  :key="q.questionId"
                  class="answer-summary-question"
                >
                  <header>
                    <span class="answer-summary-question-label">Question {{ q.questionOrder }}</span>
                    <span class="answer-summary-question-meta">
                      {{ q.responseType }} · {{ q.totalResponses }}
                      {{ q.totalResponses === 1 ? 'response' : 'responses' }}
                    </span>
                  </header>
                  <ul class="answer-summary-bars">
                    <li v-for="ans in q.topAnswers" :key="ans.value" class="answer-summary-bar">
                      <span class="answer-summary-bar-label" :title="ans.value">{{ ans.value }}</span>
                      <div class="answer-summary-bar-track">
                        <div
                          class="answer-summary-bar-fill"
                          :style="{ width: `${Math.round((ans.count / Math.max(q.totalResponses, 1)) * 100)}%` }"
                        />
                      </div>
                      <span class="answer-summary-bar-count">{{ ans.count }}</span>
                    </li>
                  </ul>
                </article>
              </div>
              <div class="answer-summary-actions">
                <button type="button" class="ghost-button" @click="responsesModalOpen = true">
                  View full responses
                </button>
              </div>
            </template>
          </div>
        </details>

        <details
          v-if="campaignKind === 'ar'"
          class="type-tile"
          :open="arSectionOpen"
          @toggle="arSectionOpen = ($event.target as HTMLDetailsElement).open"
        >
          <summary>
            <span class="type-tile-title">AR tracking telemetry</span>
            <span class="type-tile-hint">{{ arSectionOpen ? 'Hide' : 'Show' }}</span>
          </summary>
          <div class="type-tile-body">
            <p class="muted-message">Marker detections, session duration, and retry counts — coming in the next update.</p>
          </div>
        </details>
      </section>

      <SurveyResponsesModal
        :visible="responsesModalOpen"
        :campaign-id="focusCampaign?.id || ''"
        :campaign-name="focusCampaign?.name || 'Campaign'"
        @close="responsesModalOpen = false"
      />

      <section class="analytics-grid analytics-grid--overview">
        <!-- Live ratios panel hidden from view per product decision (2026-06-03):
             the "claim rate" framing is being reworked. Code retained for a
             later re-introduction — flip v-if back to a real condition then. -->
        <div v-if="false" class="overview-stack">
          <article class="surface feature-card feature-card--compact">
            <header class="panel-header">
              <div>
                <h2>Live ratios</h2>
                <p>Claim, redemption, and share conversion derived from current tallies.</p>
              </div>
            </header>

            <div class="ratio-grid ratio-grid--stacked">
              <div v-for="ratio in ratioCards" :key="ratio.label" class="ratio-card">
                <span>{{ ratio.label }}</span>
                <strong>{{ ratio.value }}</strong>
                <small>{{ ratio.detail }}</small>
              </div>
            </div>
          </article>
        </div>

        <div class="overview-stack overview-stack--main">
          <article class="surface feature-card">
            <header class="panel-header panel-header--with-actions">
              <div>
                <h2>Recent activity</h2>
                <p class="activity-panel-copy">
                  <span v-for="(line, index) in activityPanelLines" :key="`activity-panel-line-${index}`" class="activity-panel-line">
                    {{ line }}
                  </span>
                </p>
              </div>
              <div class="panel-header-actions">
                <span class="panel-header-meta">{{ recentActivity.length }} event{{ recentActivity.length === 1 ? '' : 's' }}</span>
                <button
                  type="button"
                  class="ghost-button ghost-button--small"
                  :disabled="!recentActivity.length"
                  @click="showActivityModal = true"
                >
                  View activity
                </button>
              </div>
            </header>
            <p v-if="!recentActivity.length" class="muted-message">
              No recent stream events are available yet for the current campaign scope.
            </p>
          </article>

          <teleport to="body">
            <div v-if="showActivityModal" class="paywall-overlay" @click.self="showActivityModal = false">
              <div class="paywall-card activity-modal-card">
                <button type="button" class="modal-close-button activity-modal-close" @click="showActivityModal = false" aria-label="Close recent activity">×</button>
                <h3>Recent activity</h3>
                <p class="activity-panel-copy">
                  <span v-for="(line, index) in activityPanelLines" :key="`modal-activity-line-${index}`" class="activity-panel-line">
                    {{ line }}
                  </span>
                </p>
                <div class="activity-list activity-list--scrollable">
                  <div v-for="event in recentActivity" :key="event.id" class="activity-row">
                    <strong class="activity-event">{{ event.label }}</strong>
                    <span class="activity-actor-type">{{ formatActivityActorType(event) }}</span>
                    <p class="activity-ids">{{ formatActivityIds(event, focusCampaign?.id, lookupCampaignName) }}</p>
                    <time :datetime="event.occurredAt">{{ formatDateTime(event.occurredAt) }}</time>
                  </div>
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </section>

      <!-- Advanced analytics section hidden for external users -->
      <section v-if="false" class="analytics-grid analytics-grid--advanced">
        <article v-if="activeSection === 'overview'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Overview</h2>
              <p>Campaign-first advanced view with rollups, live ratios, and ranked actors.</p>
            </div>
            <span class="panel-meta">{{ lastUpdatedLabel }}</span>
          </header>

          <div class="ratio-grid ratio-grid--compact">
            <div v-for="card in executiveCards" :key="card.label" class="ratio-card ratio-card--traffic">
              <span>{{ card.label }}</span>
              <strong>{{ card.value }}</strong>
              <small>{{ card.detail }}</small>
              <b class="traffic-dot" :class="`traffic-dot--${card.tone}`" />
            </div>
          </div>

          <div v-if="historicalPreview?.series?.length" class="detail-layout detail-layout--wide detail-layout--flush-bottom">
            <div class="stack-panel">
              <h3>Users to claims by day</h3>
              <div class="history-chart">
                <div
                  v-for="point in visitsToClaimsSeries"
                  :key="`${point.date}-visits-claims`"
                  class="history-chart-row history-chart-row--double"
                >
                  <span>{{ point.date }}</span>
                  <div class="history-chart-stack">
                    <div class="history-mini-stat">
                      <label>Users</label>
                      <div class="history-chart-track">
                        <div class="history-chart-fill" :style="{ width: `${getRelativeWidth(point.users, visitsToClaimsMaxValue)}%` }" />
                      </div>
                    </div>
                    <div class="history-mini-stat">
                      <label>Claims</label>
                      <div class="history-chart-track">
                        <div class="history-chart-fill history-chart-fill--muted" :style="{ width: `${getRelativeWidth(point.claims, visitsToClaimsMaxValue)}%` }" />
                      </div>
                    </div>
                  </div>
                  <strong>{{ formatNumber(point.users) }} / {{ formatNumber(point.claims) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="dual-grid">
            <div class="stack-panel">
              <h3>Event mix</h3>
              <div class="bar-stack">
                <div v-for="bar in eventMixBars" :key="bar.label" class="bar-row">
                  <div class="bar-copy">
                    <span>{{ bar.label }}</span>
                    <strong>{{ formatNumber(bar.value) }}</strong>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" :style="{ width: `${bar.width}%` }" />
                  </div>
                </div>
              </div>
            </div>

            <div class="stack-panel">
              <h3>Leaderboards</h3>
              <div v-if="leaderboardCards.length" class="leaderboard-grid">
                <section v-for="board in leaderboardCards" :key="board.label" class="leaderboard-card">
                  <header>
                    <h4>{{ board.label }}</h4>
                    <span>{{ board.entries.length }} entries</span>
                  </header>
                  <div v-if="board.entries.length" class="leaderboard-list">
                    <div v-for="entry in board.entries.slice(0, 5)" :key="`${board.label}-${entry.actorId}`" class="leaderboard-row">
                      <span>{{ entry.handle || `${entry.actorType} · ${shortActorId(entry.actorId)}` }}</span>
                      <strong>
                        <span class="leaderboard-score-label">Best</span> {{ formatNumber(entry.bestScore ?? entry.score) }}
                        <span class="leaderboard-score-label">Last</span> {{ formatNumber(entry.lastScore ?? entry.score) }}
                      </strong>
                    </div>
                  </div>
                  <p v-else class="muted-message">No leaderboard data yet.</p>
                </section>
              </div>
            </div>
          </div>

          <div class="detail-layout detail-layout--wide">
            <div class="stack-panel">
              <h3>Traffic lights</h3>
              <div class="signal-list">
                <div v-for="item in trafficLights" :key="item.label" class="signal-row signal-row--traffic">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <div class="traffic-value">
                    <b class="traffic-dot" :class="`traffic-dot--${item.tone}`" />
                    <span>{{ item.value }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="stack-panel">
              <h3>Drop-off and billing watch</h3>
              <div class="signal-list">
                <div v-for="item in dropOffCards" :key="item.label" class="signal-row">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <span>{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </article>

        <article v-else-if="activeSection === 'journeys'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Journey explorer</h2>
              <p>Recent journey groupings derived from canonical campaign stream data.</p>
            </div>
          </header>

          <div v-if="!hasJourneyExplorerAccess" class="muted-message">
            Journey explorer is gated by the organisation feature set.
          </div>
          <div v-else-if="journeySummaries.length" class="detail-layout">
            <div class="entity-list entity-list--selectable">
              <button
                v-for="journey in journeySummaries"
                :key="journey.id"
                type="button"
                class="entity-card entity-card--interactive"
                :class="{ active: selectedJourneySummary?.id === journey.id }"
                @click="selectedJourneyId = journey.id"
              >
                <div class="entity-head">
                  <strong>{{ journey.id }}</strong>
                  <span>{{ journey.eventCount }} events</span>
                </div>
                <p>{{ journey.latestEvent }}</p>
                <div class="entity-meta">
                  <span>Started {{ formatDateTime(journey.firstSeen) }}</span>
                  <span>Last seen {{ formatDateTime(journey.lastSeen) }}</span>
                  <span>{{ journey.issueLabel }}</span>
                </div>
              </button>
            </div>

            <div v-if="selectedJourneySummary" class="detail-panel">
              <div class="detail-panel__header">
                <div>
                  <h3>{{ selectedJourneySummary?.id }}</h3>
                  <p>{{ selectedJourneySummary?.issueLabel }}</p>
                  <p v-if="selectedJourneySummary?.dropOffHint" class="muted-message">
                    {{ selectedJourneySummary?.dropOffHint }}
                  </p>
                </div>
                <div class="detail-stats">
                  <span>{{ selectedJourneySummary?.eventCount }} events</span>
                  <span>{{ formatDateTime(selectedJourneySummary?.lastSeen ?? '') }}</span>
                </div>
              </div>

              <div v-if="selectedJourneyMetrics.length" class="detail-metrics">
                <span v-for="metric in selectedJourneyMetrics" :key="metric.label" class="detail-metric">
                  <strong>{{ metric.value }}</strong>
                  <small>{{ metric.label }}</small>
                </span>
              </div>

              <div v-if="selectedJourneyInsights.length" class="insight-list">
                <div v-for="insight in selectedJourneyInsights" :key="insight.label" class="insight-chip">
                  <strong>{{ insight.value }}</strong>
                  <small>{{ insight.label }}</small>
                </div>
              </div>

              <div v-if="selectedJourneyTransitions.length" class="stack-panel">
                <h3>Transition hotspots</h3>
                <div class="signal-list">
                  <div v-for="transition in selectedJourneyTransitions" :key="transition.label" class="signal-row">
                    <div>
                      <strong>{{ transition.label }}</strong>
                      <p>{{ transition.description }}</p>
                    </div>
                    <span>{{ transition.value }}</span>
                  </div>
                </div>
              </div>

              <div v-if="selectedJourneyEvents.length" class="timeline-list">
                <div v-for="event in selectedJourneyEvents" :key="event.id" class="timeline-row">
                  <div class="timeline-dot" />
                  <div class="timeline-copy">
                    <strong>{{ event.label }}</strong>
                    <p>{{ event.actorType }} · {{ shortActorId(event.actorId) }} · {{ event.sessionId || 'No session' }}</p>
                  </div>
                  <time :datetime="event.occurredAt">{{ formatDateTime(event.occurredAt) }}</time>
                </div>
              </div>
              <p v-else class="muted-message">
                Summary data is available for this journey, but no scoped timeline events are present yet in the current realtime window.
              </p>
            </div>
          </div>
          <p v-else class="muted-message">
            Journey identifiers are not yet present in the recent activity stream for this scope.
          </p>
        </article>

        <article v-else-if="activeSection === 'sessions'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Session explorer</h2>
              <p>Recent session groupings with event volume and latest canonical touchpoint.</p>
            </div>
          </header>

          <div v-if="sessionSummaries.length" class="detail-layout">
            <div class="entity-list entity-list--selectable">
              <button
                v-for="session in sessionSummaries"
                :key="session.id"
                type="button"
                class="entity-card entity-card--interactive"
                :class="{ active: selectedSessionSummary?.id === session.id }"
                @click="selectedSessionId = session.id"
              >
                <div class="entity-head">
                  <strong>{{ session.id }}</strong>
                  <span>{{ session.eventCount }} events</span>
                </div>
                <p>{{ session.latestEvent }}</p>
                <div class="entity-meta">
                  <span>Started {{ formatDateTime(session.firstSeen) }}</span>
                  <span>Last seen {{ formatDateTime(session.lastSeen) }}</span>
                  <span>{{ session.issueLabel }}</span>
                </div>
              </button>
            </div>

            <div v-if="selectedSessionSummary" class="detail-panel">
              <div class="detail-panel__header">
                <div>
                  <h3>{{ selectedSessionSummary?.id }}</h3>
                  <p>{{ selectedSessionSummary?.issueLabel }}</p>
                </div>
                <div class="detail-stats">
                  <span>{{ selectedSessionSummary?.eventCount }} events</span>
                  <span>{{ formatDateTime(selectedSessionSummary?.lastSeen ?? '') }}</span>
                </div>
              </div>

              <div v-if="selectedSessionMetrics.length" class="detail-metrics">
                <span v-for="metric in selectedSessionMetrics" :key="metric.label" class="detail-metric">
                  <strong>{{ metric.value }}</strong>
                  <small>{{ metric.label }}</small>
                </span>
              </div>

              <div v-if="selectedSessionInsights.length" class="insight-list">
                <div v-for="insight in selectedSessionInsights" :key="insight.label" class="insight-chip">
                  <strong>{{ insight.value }}</strong>
                  <small>{{ insight.label }}</small>
                </div>
              </div>

              <div v-if="selectedSessionEvents.length" class="timeline-list">
                <div v-for="event in selectedSessionEvents" :key="event.id" class="timeline-row">
                  <div class="timeline-dot" />
                  <div class="timeline-copy">
                    <strong>{{ event.label }}</strong>
                    <p>{{ event.actorType }} · {{ shortActorId(event.actorId) }} · {{ event.journeyId || 'No journey' }}</p>
                  </div>
                  <time :datetime="event.occurredAt">{{ formatDateTime(event.occurredAt) }}</time>
                </div>
              </div>
              <p v-else class="muted-message">
                Summary data is available for this session, but no scoped timeline events are present yet in the current realtime window.
              </p>
            </div>
          </div>
          <p v-else class="muted-message">
            Session identifiers are not yet available in the recent stream for this campaign.
          </p>
        </article>

        <article v-else-if="activeSection === 'events'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Event stream</h2>
              <p>Recent live analytics events for the current campaign scope.</p>
            </div>
          </header>

          <div class="ratio-grid ratio-grid--compact">
            <div class="ratio-card">
              <span>Tracked events</span>
              <strong>{{ recentActivity.length }}</strong>
              <small>Current stream window</small>
            </div>
            <div class="ratio-card">
              <span>Journey gaps</span>
              <strong>{{ eventDiagnostics.missingJourneyCount }}</strong>
              <small>Events without journey context</small>
            </div>
            <div class="ratio-card">
              <span>Session gaps</span>
              <strong>{{ eventDiagnostics.missingSessionCount }}</strong>
              <small>Events without session context</small>
            </div>
            <div class="ratio-card">
              <span>Error-like events</span>
              <strong>{{ eventDiagnostics.streamIssues }}</strong>
              <small>Blocked, denied, failed, or error signals</small>
            </div>
          </div>

          <div v-if="recentActivity.length" class="events-table">
            <div class="events-row events-row--head">
              <span>Time</span>
              <span>Event</span>
              <span>Actor</span>
              <span>Journey</span>
              <span>Session</span>
              <span>Request</span>
            </div>
            <div v-for="event in recentActivity" :key="event.id" class="events-row">
              <span>{{ formatDateTime(event.occurredAt) }}</span>
              <span>{{ event.label }}</span>
              <span>{{ event.actorType || 'unknown' }} · {{ shortActorId(event.actorId) }}</span>
              <span>{{ event.journeyId || '—' }}</span>
              <span>{{ event.sessionId || '—' }}</span>
              <span>{{ event.requestId || '—' }}</span>
            </div>
          </div>
          <p v-else class="muted-message">
            No event stream has been recorded yet for this campaign scope.
          </p>
        </article>

        <article v-else-if="activeSection === 'messages'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Canonical messages</h2>
              <p>
                The canonical analytics event mix, driven by the app-native historical preview and
                the recent realtime stream.
              </p>
            </div>
            <div v-if="hasOpenSearchAccess" class="panel-actions">
              <button type="button" class="ghost-button" @click="openOpenSearchAddon">
                Open OpenSearch add-on
              </button>
            </div>
          </header>

          <div v-if="historicalPreview" class="ratio-grid ratio-grid--compact">
            <div class="ratio-card">
              <span>Historical events</span>
              <strong>{{ formatNumber(historicalPreview?.totals?.totalEvents ?? 0) }}</strong>
              <small>{{ historicalPreview?.window?.days }} day preview window</small>
            </div>
            <div class="ratio-card">
              <span>Top message</span>
              <strong>{{ topCanonicalMessage.label }}</strong>
              <small>{{ formatNumber(topCanonicalMessage.count) }} occurrences</small>
            </div>
            <div class="ratio-card">
              <span>Journey coverage</span>
              <strong>{{ formatPercent(messageCoverage.journeyCoverage) }}</strong>
              <small>{{ formatNumber(messageCoverage.withJourneyId) }} events with journey context</small>
            </div>
            <div class="ratio-card">
              <span>Session coverage</span>
              <strong>{{ formatPercent(messageCoverage.sessionCoverage) }}</strong>
              <small>{{ formatNumber(messageCoverage.withSessionId) }} events with session context</small>
            </div>
            <div class="ratio-card">
              <span>Request coverage</span>
              <strong>{{ formatPercent(messageCoverage.requestCoverage) }}</strong>
              <small>{{ formatNumber(messageCoverage.withRequestId) }} events with request context</small>
            </div>
          </div>

          <div v-if="canonicalMessageBars.length" class="detail-layout detail-layout--wide">
            <div class="stack-panel">
              <h3>Canonical event names</h3>
              <div class="bar-stack">
                <div v-for="bar in canonicalMessageBars" :key="bar.label" class="bar-row">
                  <div class="bar-copy">
                    <span>{{ bar.label }}</span>
                    <strong>{{ formatNumber(bar.value) }}</strong>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" :style="{ width: `${bar.width}%` }" />
                  </div>
                </div>
              </div>
            </div>

            <div class="stack-panel">
              <h3>Coverage notes</h3>
              <div class="signal-list">
                <div class="signal-row">
                  <div>
                    <strong>Billable session ready</strong>
                    <p>Use this screen to validate session, journey, request, and later billable session coverage.</p>
                  </div>
                  <span>{{ historicalPreview ? formatNumber(historicalPreview?.instrumentationCoverage?.withSessionId ?? 0) : '0' }}</span>
                </div>
                <div class="signal-row">
                  <div>
                    <strong>Geo-tagged events</strong>
                    <p>Tracks events carrying IP-derived or profile-level geo metadata.</p>
                  </div>
                  <span>{{ historicalPreview ? formatNumber(historicalPreview?.instrumentationCoverage?.geoTaggedEvents ?? 0) : '0' }}</span>
                </div>
                <div class="signal-row">
                  <div>
                    <strong>GPS-tagged events</strong>
                    <p>Tracks higher-confidence device coordinates separately from generic geo tagging.</p>
                  </div>
                  <span>{{ historicalPreview ? formatNumber(historicalPreview?.instrumentationCoverage?.gpsTaggedEvents ?? 0) : '0' }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="recentActivity.length" class="events-table">
            <div class="events-row events-row--head">
              <span>Time</span>
              <span>Canonical event</span>
              <span>Actor</span>
              <span>Journey</span>
              <span>Session</span>
              <span>Request</span>
            </div>
            <div v-for="event in recentActivity" :key="event.id" class="events-row">
              <span>{{ formatDateTime(event.occurredAt) }}</span>
              <span>{{ event.label }}</span>
              <span>{{ event.actorType || 'unknown' }} · {{ shortActorId(event.actorId) }}</span>
              <span>{{ event.journeyId || '—' }}</span>
              <span>{{ event.sessionId || '—' }}</span>
              <span>{{ event.requestId || '—' }}</span>
            </div>
          </div>
          <p v-else-if="historicalPreview" class="muted-message">
            Historical canonical event coverage is available, but the recent realtime stream is empty for this campaign scope.
          </p>
          <p v-else class="muted-message">
            Canonical message analytics will appear here once historical preview data is available for this scope.
          </p>
        </article>

        <article v-else-if="activeSection === 'diagnostics'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Diagnostics</h2>
              <p>Operational signals derived from lifecycle state and recent analytics events.</p>
            </div>
          </header>

          <div v-if="!hasDiagnosticsAccess" class="muted-message">
            Diagnostics is gated by the organisation feature set.
          </div>
          <div v-else-if="diagnostics.length || diagnosticGroups.operations.some(item => item.value > 0) || diagnosticGroups.observability.some(item => item.value > 0)" class="detail-layout">
            <div class="stack-panel">
              <h3>Campaign health</h3>
              <div class="ratio-grid ratio-grid--compact">
                <div v-for="card in campaignHealthCards" :key="card.label" class="ratio-card">
                  <span>{{ card.label }}</span>
                  <strong>{{ card.value }}</strong>
                  <small>{{ card.detail }}</small>
                </div>
              </div>
              <h3>Observability</h3>
              <div class="signal-list">
                <div v-for="item in diagnosticGroups.observability" :key="item.label" class="signal-row">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <span>{{ item.value }}</span>
                </div>
              </div>
              <h3>Operations</h3>
              <div class="signal-list">
                <div v-for="item in diagnosticGroups.operations" :key="item.label" class="signal-row">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <span>{{ item.value }}</span>
                </div>
              </div>
              <h3>Security</h3>
              <div class="signal-list">
                <div v-for="item in diagnosticGroups.security" :key="item.label" class="signal-row">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <span>{{ item.value }}</span>
                </div>
              </div>
              <h3>Activity health</h3>
              <div class="signal-list">
                <div v-for="item in diagnosticGroups.activity" :key="item.label" class="signal-row">
                  <div>
                    <strong>{{ item.label }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                  <span>{{ item.value }}</span>
                </div>
              </div>
            </div>

            <div class="diagnostics-column">
              <div v-if="diagnosticHighlights.length" class="stack-panel">
                <h3>Priority signals</h3>
                <div class="signal-list">
                  <div v-for="item in diagnosticHighlights" :key="item.label" class="signal-row">
                    <div>
                      <strong>{{ item.label }}</strong>
                      <p>{{ item.description }}</p>
                    </div>
                    <span>{{ item.value }}</span>
                  </div>
                </div>
              </div>

              <div v-if="diagnosticEventFamilies.length" class="stack-panel">
                <h3>Error families</h3>
                <div class="signal-list">
                  <div v-for="item in diagnosticEventFamilies" :key="item.label" class="signal-row">
                    <div>
                      <strong>{{ item.label }}</strong>
                      <p>Recent repeated failure or denied signal.</p>
                    </div>
                    <span>{{ item.count }}</span>
                  </div>
                </div>
              </div>

              <div class="diagnostics-list">
              <div v-for="item in diagnostics" :key="item.title" class="diagnostic-card" :class="item.level">
                <strong>{{ item.title }}</strong>
                <p>{{ item.description }}</p>
              </div>
              </div>
            </div>
          </div>
          <p v-else class="muted-message">
            No active issues were inferred from the current campaign state and stream.
          </p>
        </article>

        <article v-else-if="activeSection === 'geo' && showGeoSurfaces" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Geo</h2>
              <p>Native geo and GPS points derived from canonical analytics events.</p>
            </div>
          </header>

          <div v-if="!hasGeoAccess" class="muted-message">
            Geo analytics is gated by the organisation feature set.
          </div>
          <div v-else-if="geoPoints.length" class="detail-layout detail-layout--wide detail-layout--flush-bottom">
            <div class="stack-panel">
              <header class="panel-header panel-header--compact">
                <div>
                  <h3>Geo footprint</h3>
                  <p>Coarse geo-tagged activity, typically IP-derived or general analytics geo context.</p>
                </div>
              </header>
              <svg viewBox="0 0 100 50" class="geo-map" role="img" aria-label="Geo activity plot">
                <rect x="0" y="0" width="100" height="50" rx="2" class="geo-map__bg" />
                <g class="geo-map__grid">
                  <line x1="25" y1="0" x2="25" y2="50" />
                  <line x1="50" y1="0" x2="50" y2="50" />
                  <line x1="75" y1="0" x2="75" y2="50" />
                  <line x1="0" y1="12.5" x2="100" y2="12.5" />
                  <line x1="0" y1="25" x2="100" y2="25" />
                  <line x1="0" y1="37.5" x2="100" y2="37.5" />
                </g>
                <circle
                  v-for="point in geoOnlyPoints"
                  :key="`${point.id}-geo`"
                  :cx="point.x"
                  :cy="point.y"
                  :r="point.radius"
                  class="geo-map__point"
                  :class="'geo-map__point--geo'"
                />
              </svg>
            </div>

            <div class="stack-panel">
              <header class="panel-header panel-header--compact">
                <div>
                  <h3>GPS and precise placement</h3>
                  <p>Higher-confidence device GPS and explicit campaign-location activity.</p>
                </div>
              </header>
              <svg viewBox="0 0 100 50" class="geo-map" role="img" aria-label="GPS activity plot">
                <rect x="0" y="0" width="100" height="50" rx="2" class="geo-map__bg" />
                <g class="geo-map__grid">
                  <line x1="25" y1="0" x2="25" y2="50" />
                  <line x1="50" y1="0" x2="50" y2="50" />
                  <line x1="75" y1="0" x2="75" y2="50" />
                  <line x1="0" y1="12.5" x2="100" y2="12.5" />
                  <line x1="0" y1="25" x2="100" y2="25" />
                  <line x1="0" y1="37.5" x2="100" y2="37.5" />
                </g>
                <circle
                  v-for="point in preciseGeoPoints"
                  :key="`${point.id}-precise`"
                  :cx="point.x"
                  :cy="point.y"
                  :r="point.radius"
                  class="geo-map__point"
                  :class="point.source === 'campaign_location' ? 'geo-map__point--campaign' : 'geo-map__point--gps'"
                />
              </svg>
            </div>
          </div>

          <div v-if="geoPoints.length" class="detail-layout detail-layout--geo">
            <div class="stack-panel">
              <div class="density-grid">
                <div v-for="cell in geoDensityCells" :key="cell.id" class="density-card">
                  <span>{{ cell.label }}</span>
                  <strong>{{ cell.count }}</strong>
                </div>
              </div>
            </div>
            <div class="geo-sidebar">
              <div class="geo-stat">
                <span>Plotted points</span>
                <strong>{{ geoSummary.total }}</strong>
              </div>
              <div class="geo-stat">
                <span>Geo source</span>
                <strong>{{ geoSummary.geoCount }} geo · {{ geoSummary.gpsCount }} gps · {{ geoSummary.campaignLocationCount }} campaign</strong>
              </div>
              <div class="density-grid density-grid--sources">
                <div v-for="source in geoSourceCards" :key="source.label" class="density-card">
                  <span>{{ source.label }}</span>
                  <strong>{{ source.count }}</strong>
                </div>
              </div>
              <div v-if="geoActivity.length" class="geo-activity-list">
                <div v-for="activity in geoActivity" :key="activity.id" class="geo-activity-row">
                  <strong>{{ activity.label }}</strong>
                  <span>{{ formatDateTime(activity.occurredAt) }} · {{ activity.geoPoint?.source || 'geo' }}</span>
                </div>
              </div>
              <p class="muted-message">
                This first cut plots canonical `geo.*` and `gps.*` fields in the realtime stream and groups density by coarse map cells. IP-derived geo enrichment still needs instrumentation.
              </p>
            </div>
          </div>
          <div v-else-if="historicalGeoFallback" class="detail-layout detail-layout--geo">
            <div class="stack-panel">
              <header class="panel-header panel-header--compact">
                <div>
                  <h3>Historical geo fallback</h3>
                  <p>Realtime geo is empty for this campaign, so this view is using recent historical data instead.</p>
                </div>
              </header>

              <div class="density-grid density-grid--sources">
                <div v-for="source in historicalGeoSourceCards" :key="source.label" class="density-card">
                  <span>{{ source.label }}</span>
                  <strong>{{ source.count }}</strong>
                </div>
              </div>

              <div class="history-chart">
                <div v-for="point in historicalGeoFallback?.geoSeries" :key="point.date" class="history-chart-row history-chart-row--double">
                  <span>{{ point.date }}</span>
                  <div class="history-chart-stack">
                    <div class="history-mini-stat">
                      <label>All geo points</label>
                      <div class="history-chart-track">
                        <div class="history-chart-fill" :style="{ width: `${getRelativeWidth(point.geoPoints, historicalGeoMaxValue)}%` }" />
                      </div>
                    </div>
                    <div class="history-mini-stat">
                      <label>GPS / campaign location</label>
                      <div class="history-chart-track">
                        <div class="history-chart-fill history-chart-fill--muted" :style="{ width: `${getRelativeWidth(point.gpsPoints + point.campaignLocationPoints, historicalGeoMaxValue)}%` }" />
                      </div>
                    </div>
                  </div>
                  <strong>{{ formatNumber(point.geoPoints) }} · {{ formatNumber(point.gpsPoints) }}/{{ formatNumber(point.campaignLocationPoints) }}</strong>
                </div>
              </div>
            </div>

            <div class="geo-sidebar">
              <div class="geo-stat">
                <span>Historical geo points</span>
                <strong>{{ historicalGeoFallback?.totals?.geoPoints }}</strong>
              </div>
              <div class="geo-stat">
                <span>Window</span>
                <strong>{{ historicalGeoFallback?.window?.days }} days</strong>
              </div>
              <div class="geo-stat">
                <span>Parsed records</span>
                <strong>{{ historicalGeoFallback?.sourceSummary?.recordsParsed }}</strong>
              </div>
              <p class="muted-message">
                This fallback keeps the Geo tab useful when the current realtime stream window has no geo-capable events. It will switch back to realtime plotting as soon as fresh geo events arrive.
              </p>
            </div>
          </div>
          <p v-else class="muted-message">
            No canonical geo or GPS points are available yet in the current realtime stream. This section is native-only now and will light up as instrumentation lands.
          </p>
        </article>

        <article v-else-if="activeSection === 'leaderboards'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Leaderboards</h2>
              <p>Valkey-backed leaderboard snapshots proxied through the cache manager for the current campaign.</p>
            </div>
          </header>

          <div class="ratio-grid ratio-grid--compact">
            <div v-for="card in leaderboardSummaryCards" :key="card.label" class="ratio-card">
              <span>{{ card.label }}</span>
              <strong>{{ card.value }}</strong>
              <small>{{ card.detail }}</small>
            </div>
          </div>

          <div class="leaderboard-grid leaderboard-grid--wide">
            <section v-for="board in leaderboardCards" :key="board.label" class="leaderboard-card">
              <header>
                <h4>{{ board.label }}</h4>
                <span>{{ board.entries.length }} rows</span>
              </header>
              <div v-if="board.entries.length" class="leaderboard-list">
                <div v-for="(entry, index) in board.entries" :key="`${board.label}-${entry.actorId}-${index}`" class="leaderboard-row leaderboard-row--detailed">
                  <span>#{{ index + 1 }} · {{ entry.handle || `${entry.actorType} · ${shortActorId(entry.actorId)}` }}</span>
                  <strong>
                    <span class="leaderboard-score-label">Best</span> {{ formatNumber(entry.bestScore ?? entry.score) }}
                    <span class="leaderboard-score-label">Last</span> {{ formatNumber(entry.lastScore ?? entry.score) }}
                  </strong>
                </div>
              </div>
              <p v-else class="muted-message">No leaderboard entries yet for this board.</p>
            </section>
          </div>

          <div v-if="leaderboardRows.length" class="events-table">
            <div class="events-row events-row--head leaderboard-table">
              <span>Board</span>
              <span>Rank</span>
              <span>Actor type</span>
              <span>Actor</span>
              <span>Score</span>
              <span>Notes</span>
            </div>
            <div v-for="row in leaderboardRows" :key="row.key" class="events-row leaderboard-table">
              <span>{{ row.board }}</span>
              <span>#{{ row.rank }}</span>
              <span>{{ row.actorType }}</span>
              <span>{{ shortActorId(row.actorId) }}</span>
              <span>{{ formatNumber(row.score) }}</span>
              <span>Valkey leaderboard snapshot</span>
            </div>
          </div>
          <p v-else class="muted-message">
            No leaderboard data has been returned yet for the current campaign scope.
          </p>
        </article>

        <article v-else-if="activeSection === 'exports'" class="surface feature-card feature-card--full">
          <header class="panel-header">
            <div>
              <h2>Export centre</h2>
              <p>Paywalled extraction for current native analytics datasets.</p>
            </div>
            <button type="button" class="ghost-button ghost-button--small" @click="openHistoricalAnalytics">
              Historical analytics
            </button>
          </header>

          <div v-if="!hasExportsAccess" class="muted-message">
            Export access is gated by the organisation feature set.
          </div>
          <div v-else class="export-grid">
            <div v-for="card in exportCards" :key="card.id" class="export-card">
              <div>
                <strong>{{ card.label }}</strong>
                <p>{{ card.detail }}</p>
              </div>
              <button
                type="button"
                class="ghost-button ghost-button--small"
                :disabled="!card.enabled"
                @click="card.enabled && isExportDataset(card.id) ? exportDataset(card.id) : undefined"
              >
                {{ card.enabled ? card.actionLabel || 'Download CSV' : 'Not ready' }}
              </button>
            </div>
            <div v-if="exportJobs.length" class="export-jobs">
              <div v-for="job in exportJobs" :key="job.id" class="export-job-row">
                <div>
                  <strong>{{ job.fileName || `${job.dataset} export` }}</strong>
                  <p>
                    {{ formatRelativeTime(job.updatedAt) }} · {{ job.status }}
                    <span v-if="typeof job.progressPercent === 'number'"> · {{ job.progressPercent }}%</span>
                  </p>
                </div>
                <button
                  v-if="job.status === 'COMPLETE'"
                  type="button"
                  class="ghost-button ghost-button--small"
                  @click="downloadExportJob(job.id)"
                >
                  Download
                </button>
                <span v-else class="muted-message">
                  {{ job.errorMessage || (job.status === 'QUEUED' ? 'Queued…' : 'Processing…') }}
                </span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section v-if="false" class="surface addon-entry">
        <header class="panel-header">
          <div>
            <h2>Add-ons and historical</h2>
            <p>
              OpenSearch and the future DuckDB-backed historical layer live outside the native realtime workspace
              so this screen can stay focused on canonical analytics data.
            </p>
          </div>
          <div class="addon-actions">
            <button type="button" class="ghost-button" @click="openHistoricalAnalytics">
              Historical analytics
            </button>
            <button type="button" class="primary-button" @click="openVisualBoard">
              Visual board
            </button>
            <button type="button" class="ghost-button" @click="openOpenSearchAddon">
              Open add-on
            </button>
          </div>
        </header>
        <p class="muted-message">
          <template v-if="hasOpenSearchAccess">
            Access is enabled for this organisation. Step-function lifecycle status still needs explicit control-plane wiring.
          </template>
          <template v-else>
            This organisation does not currently have the OpenSearch add-on entitlement.
          </template>
        </p>
      </section>

    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { billingService, type OrgDetails } from '@/services/billing'
import {
  analyticsService,
  type AnalyticsExportDataset,
  type AnalyticsExportJob,
  type HistoricalAnalyticsQuery,
  type AnalyticsLeaderboardEntry,
  type AnalyticsRelatedSummary,
  type AnalyticsScopeSummary,
  type AnalyticsStreamRecord,
} from '@/services/analytics'
import { campaignsService, type ManagedCampaign } from '@/services/campaigns'
import { useAuthStore } from '@/store/auth.store'
import { Line, Bar } from 'vue-chartjs'
import SurveyResponsesModal from '@/components/SurveyResponsesModal.vue'
import AnalyticsRangePicker, { type RangeSpec } from '@/components/AnalyticsRangePicker.vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler)

type ChartWindowId = '24h' | '3d' | '7d'
const CHART_WINDOWS: Array<{ id: ChartWindowId; label: string; days: number; granularity: 'hour' | 'day' }> = [
  { id: '24h', label: '24 hours', days: 1, granularity: 'hour' },
  { id: '3d', label: '3 days', days: 3, granularity: 'hour' },
  { id: '7d', label: '7 days', days: 7, granularity: 'day' },
]

type OverTimeRangeId = 'today' | '7d' | '30d'
const OVER_TIME_RANGES: Array<{ id: OverTimeRangeId; label: string; days: number; granularity: 'hour' | 'day' }> = [
  { id: 'today', label: 'Today', days: 1, granularity: 'hour' },
  { id: '7d', label: '7 days', days: 7, granularity: 'day' },
  { id: '30d', label: '30 days', days: 30, granularity: 'day' },
]

type CampaignStatus = 'Active' | 'Scheduled' | 'Paused' | 'Ended' | 'Unscheduled'
type AnalyticsMode = 'standard' | 'advanced'
type AdvancedSection = 'overview' | 'journeys' | 'sessions' | 'events' | 'messages' | 'diagnostics' | 'geo' | 'leaderboards' | 'exports'

type AnalyticsCampaign = ManagedCampaign & {
  lifecycleStatus: CampaignStatus
  metrics: {
    registered: number
    claimed: number
    redeemed: number
    shared: number
  }
}

type ActivityRecord = {
  id: string
  label: string
  occurredAt: string
  actorId: string
  actorEmail: string
  actorName: string
  actorType: string
  sessionId: string
  journeyId: string
  requestId: string
  context: string
  sourceLabel: string
  campaignId: string
  clientIp: string
  userAgent: string
  acceptedAt: string
  acceptedTermsVersion: string
  termsAndConditionsCheck: boolean | null
  rawFields: Record<string, string>
  geoPoint: {
    lat: number
    lon: number
    source: 'geo' | 'gps' | 'campaign_location'
  } | null
}

type EntitySummary = {
  id: string
  eventCount: number
  firstSeen: string
  lastSeen: string
  latestEvent: string
  issueLabel: string
  issueLevel?: 'healthy' | 'warning' | 'critical'
  dropOffHint?: string
  source?: 'native-stream' | 'related-summary'
}

type DiagnosticItem = {
  title: string
  description: string
  level: 'info' | 'warning' | 'critical'
}

type SummaryMetric = {
  label: string
  value: string
}

type HealthCard = {
  label: string
  value: string
  detail: string
  tone: 'healthy' | 'warning' | 'critical'
}

const ADVANCED_SECTIONS: Array<{ id: AdvancedSection; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'journeys', label: 'Journeys' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'events', label: 'Events' },
  { id: 'messages', label: 'Messages' },
  { id: 'diagnostics', label: 'Diagnostics' },
  { id: 'geo', label: 'Geo' },
  { id: 'leaderboards', label: 'Leaderboards' },
  { id: 'exports', label: 'Exports' },
]

const EXPORT_DATASETS: AnalyticsExportDataset[] = [
  'events',
  'historical',
  'journeys',
  'sessions',
  'geo',
  'diagnostics',
  'portfolio',
  'leaderboards',
]

const formatCampaignSelectLabel = (value?: string | null): string => {
  const label = (value || 'Untitled campaign').trim()
  if (label.length <= 34) {
    return label
  }

  const [idPart, remainder] = label.split('_', 2)
  if (remainder && idPart.length > 16) {
    return `${idPart.slice(0, 8)}...${idPart.slice(-4)} ${remainder}`
  }

  return `${label.slice(0, 31)}...`
}

const deriveLifecycleStatus = (campaign: ManagedCampaign): CampaignStatus => {
  if (campaign.paused) return 'Paused'

  const now = Date.now()
  const start = campaign.startDate ? new Date(campaign.startDate).getTime() : null
  const end = campaign.endDate ? new Date(campaign.endDate).getTime() : null

  if (!start && !end) return 'Unscheduled'
  if (start && !Number.isNaN(start) && now < start) return 'Scheduled'
  if (end && !Number.isNaN(end) && now > end) return 'Ended'
  return 'Active'
}

const toSentenceCase = (value: string): string => {
  if (!value) return 'Unknown event'
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const readString = (value: unknown): string => (typeof value === 'string' ? value : '')
const readNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)
const readCoordinate = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const getRelativeWidth = (value: number, maxValue: number): number => {
  if (!maxValue) return 0
  return Math.max(8, Math.min(100, (value / maxValue) * 100))
}

const asSummaryRecord = (value: unknown): AnalyticsScopeSummary => (
  value && typeof value === 'object' ? value as AnalyticsScopeSummary : {}
)

const SUMMARY_PRIORITY: Array<{ key: string; label: string }> = [
  { key: 'eventsProcessed', label: 'Events' },
  { key: 'user.registration.succeeded', label: 'Registrations' },
  { key: 'reward.drop.claimed', label: 'Claimed' },
  { key: 'errors', label: 'Errors' },
]

const SUMMARY_BLOCKLIST = new Set([
  'reward.drop.redeemed',
  'share.link.shared',
  'redeemed',
  'shared',
])

const parseJsonObject = (value: string): Record<string, unknown> | null => {
  if (!value) return null
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null
  } catch {
    return null
  }
}

const normalizeStreamFields = (fields: Record<string, string>): Record<string, string> => {
  const eventPayload = parseJsonObject(fields.event || '')
  if (!eventPayload) {
    return fields
  }

  const normalized: Record<string, string> = { ...fields }

  Object.entries(eventPayload).forEach(([key, value]) => {
    if (value == null) {
      return
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = String(value)
      return
    }

    if (typeof value === 'object') {
      normalized[key] = JSON.stringify(value)
    }
  })

  return normalized
}

const isErrorLikeLabel = (value: string): boolean => /error|fail|denied|blocked/i.test(value)
const isRegistrationLabel = (value: string): boolean => /registered|registration/i.test(value)
const isClaimLabel = (value: string): boolean => /claimed|reward\.drop\.claimed|reward\.loyalty\.claimed/i.test(value)
const isRedeemLabel = (value: string): boolean => /redeemed|reward\.drop\.redeemed/i.test(value)
const isShareLabel = (value: string): boolean => /shared|share|share\.link\.|share\.vat\./i.test(value)
const isEntryLabel = (value: string): boolean => /landing|page view|page-view|login-requested|logged-in|sent|navigation\.landing\.viewed|navigation\.page\.viewed|auth\.login|messaging\.(auth\.)?email\.sent/i.test(value)

const inferEntityIssue = (events: ActivityRecord[]) => {
  const labels = events.map((event) => event.label)
  const hasErrors = labels.some(isErrorLikeLabel)
  const hasRegistration = labels.some(isRegistrationLabel)
  const hasClaim = labels.some(isClaimLabel)
  const hasRedeem = labels.some(isRedeemLabel)
  const hasShare = labels.some(isShareLabel)
  const hasEntry = labels.some(isEntryLabel)
  const missingContext = events.filter((event) => !event.requestId || !event.sessionId || !event.journeyId).length

  if (hasErrors) {
    return {
      issueLevel: 'critical' as const,
      issueLabel: 'Failure signals detected',
      dropOffHint: 'Error-like events appear in this path and likely interrupted the journey.',
    }
  }

  if (hasClaim && !hasRedeem) {
    return {
      issueLevel: 'warning' as const,
      issueLabel: 'Stalled after claim',
      dropOffHint: 'Claims are present but redeems are not, which suggests fulfilment or continuation drop-off.',
    }
  }

  if (hasEntry && !hasRegistration && !hasClaim && !hasRedeem) {
    return {
      issueLevel: 'warning' as const,
      issueLabel: 'Dropped before conversion',
      dropOffHint: 'Entry or login activity occurred without progressing into registration, claim, or redeem.',
    }
  }

  if (missingContext > 0) {
    return {
      issueLevel: 'warning' as const,
      issueLabel: 'Traceability gaps detected',
      dropOffHint: 'The path has activity, but missing identifiers reduce confidence in the full trace.',
    }
  }

  if (hasRedeem || hasShare) {
    return {
      issueLevel: 'healthy' as const,
      issueLabel: 'Completed path observed',
      dropOffHint: 'This path reaches later-stage engagement or fulfilment events.',
    }
  }

  if (hasRegistration || hasClaim) {
    return {
      issueLevel: 'healthy' as const,
      issueLabel: 'Progressing normally',
      dropOffHint: 'The path is moving through conversion steps without clear failure signals.',
    }
  }

  return {
    issueLevel: 'healthy' as const,
    issueLabel: 'No active issues inferred',
    dropOffHint: 'No clear breakage pattern is visible in the current event window.',
  }
}

const extractGeoPoint = (fields: Record<string, string>) => {
  const metadata = parseJsonObject(fields.metadata || '')
  const metadataGeo = metadata?.geo && typeof metadata.geo === 'object' ? metadata.geo as Record<string, unknown> : {}
  const metadataGps = metadata?.gps && typeof metadata.gps === 'object' ? metadata.gps as Record<string, unknown> : {}
  const rawGeo = parseJsonObject(fields.geo || '')
  const rawGps = parseJsonObject(fields.gps || '')
  const vitem = parseJsonObject(fields.vitem || '')
  const itemLocation = parseJsonObject(fields.itemLocation || '')

  const geoLat = readCoordinate(fields['geo.lat'] ?? metadataGeo.lat ?? rawGeo?.lat ?? rawGeo?.latitude)
  const geoLon = readCoordinate(fields['geo.lon'] ?? metadataGeo.lon ?? rawGeo?.lon ?? rawGeo?.lng ?? rawGeo?.longitude)
  if (geoLat !== null && geoLon !== null) {
    return { lat: geoLat, lon: geoLon, source: 'geo' as const }
  }

  const gpsLat = readCoordinate(fields['gps.lat'] ?? metadataGps.lat ?? rawGps?.lat ?? rawGps?.latitude)
  const gpsLon = readCoordinate(fields['gps.lon'] ?? metadataGps.lon ?? rawGps?.lon ?? rawGps?.lng ?? rawGps?.longitude)
  if (gpsLat !== null && gpsLon !== null) {
    return { lat: gpsLat, lon: gpsLon, source: 'gps' as const }
  }

  const campaignLat = readCoordinate(
    fields['campaignLocation.lat']
    ?? itemLocation?.lat
    ?? itemLocation?.latitude
    ?? vitem?.lat
    ?? vitem?.latitude
  )
  const campaignLon = readCoordinate(
    fields['campaignLocation.lon']
    ?? itemLocation?.lon
    ?? itemLocation?.lng
    ?? itemLocation?.longitude
    ?? vitem?.lon
    ?? vitem?.lng
    ?? vitem?.longitude
  )
  if (campaignLat !== null && campaignLon !== null) {
    return { lat: campaignLat, lon: campaignLon, source: 'campaign_location' as const }
  }

  return null
}

const parseActivityRecord = (record: AnalyticsStreamRecord): ActivityRecord => {
  const fields = normalizeStreamFields(record.fields || {})
  const metadata = parseJsonObject(fields.metadata || '')
  const occurredAt = fields.occurredAt || fields.createdAt || record.id
  const actorId = fields.actorId || ''
  const eventName =
    fields.eventName ||
    fields.event ||
    readString(metadata?.legacyEventName) ||
    'event'
  const actorType = fields.actorType || 'actor'
  const sessionId = fields.sessionId || ''
  const journeyId = fields.journeyId || ''
  const requestId = fields.requestId || ''
  const campaignId = fields.campaignId || ''
  const clientIp = fields.clientIp || readString(metadata?.clientIp)
  const userAgent = fields.userAgent || readString(metadata?.userAgent)
  const acceptedAt = fields.acceptedAt || readString(metadata?.acceptedAt)
  const acceptedTermsVersion = fields.acceptedTermsVersion || readString(metadata?.acceptedTermsVersion)
  const termsAndConditionsCheck = typeof metadata?.termsAndConditionsCheck === 'boolean' ? metadata.termsAndConditionsCheck : null
  const userObj = parseJsonObject(fields.user || '')
  const actorEmail = fields.email || readString(userObj?.email)
  const firstName = readString(userObj?.firstName).trim()
  const lastName = readString(userObj?.lastName).trim()
  const actorName = [firstName, lastName].filter(Boolean).join(' ').trim()

  return {
    id: record.id,
    label: toSentenceCase(eventName),
    occurredAt,
    actorId,
    actorEmail,
    actorName,
    actorType,
    sessionId,
    journeyId,
    requestId,
    context: [actorType, actorId, journeyId].filter(Boolean).join(' · ') || 'Campaign event',
    sourceLabel: fields.source || 'Realtime analytics',
    campaignId,
    clientIp,
    userAgent,
    acceptedAt,
    acceptedTermsVersion,
    termsAndConditionsCheck,
    rawFields: fields,
    geoPoint: extractGeoPoint(fields),
  }
}

const readCount = (summary: AnalyticsScopeSummary, ...keys: string[]): number => (
  keys.reduce((max, key) => Math.max(max, readNumber(summary[key])), 0)
)

const sortByOccurredAtDesc = (left: ActivityRecord, right: ActivityRecord) => {
  return new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime()
}

const buildEntitySummaries = (activities: ActivityRecord[], key: 'journeyId' | 'sessionId'): EntitySummary[] => {
  const groups = new Map<string, ActivityRecord[]>()

  activities.forEach((activity) => {
    const value = activity[key]
    if (!value) return
    const current = groups.get(value) || []
    current.push(activity)
    groups.set(value, current)
  })

  return Array.from(groups.entries())
    .map(([id, group]) => {
      const sorted = [...group].sort(sortByOccurredAtDesc)
      const inferred = inferEntityIssue(sorted)

      return {
        id,
        eventCount: group.length,
        firstSeen: group[group.length - 1]?.occurredAt || sorted[sorted.length - 1]?.occurredAt || '',
        lastSeen: sorted[0]?.occurredAt || '',
        latestEvent: sorted[0]?.label || 'Unknown event',
        issueLabel: inferred.issueLabel,
        issueLevel: inferred.issueLevel,
        dropOffHint: inferred.dropOffHint,
      }
    })
    .sort((left, right) => right.eventCount - left.eventCount)
}

const buildRelatedEntitySummaries = (items: AnalyticsRelatedSummary[] | undefined, fallbackLabel: string): EntitySummary[] => (
  Array.isArray(items)
    ? items.map((item) => {
      const summary = item.summary || {}
      const latestEvent = readString(summary.lastEventName) || fallbackLabel
      const eventCount = readNumber(summary.__total__ ?? summary.eventsProcessed)
      const issueScore = readNumber(summary.errors)
      const issueLevel: NonNullable<EntitySummary['issueLevel']> = issueScore > 0 || /error|fail|denied|blocked/i.test(latestEvent)
        ? 'critical'
        : readCount(summary, 'reward.drop.claimed', 'claimed') > 0 && readCount(summary, 'reward.drop.redeemed', 'redeemed') === 0
          ? 'warning'
          : 'healthy'
      return {
        id: item.id,
        eventCount,
        firstSeen: readString(summary.firstOccurredAt) || readString(summary.createdAt) || readString(summary.lastOccurredAt) || '',
        lastSeen: readString(summary.lastOccurredAt) || '',
        latestEvent: toSentenceCase(latestEvent),
        issueLabel: issueLevel === 'critical'
          ? 'Failure signals detected'
          : issueLevel === 'warning'
            ? 'Stalled after claim'
            : 'No active issues inferred',
        issueLevel,
        dropOffHint: issueLevel === 'critical'
          ? 'The related summary has explicit error counts or a failure-like latest event.'
          : issueLevel === 'warning'
            ? 'Claim volume exists without matching redeems in the summary.'
            : 'No clear breakage indicators were surfaced by the related summary.',
        source: 'related-summary' as const,
      }
    }).sort((left, right) => right.eventCount - left.eventCount)
    : []
)

const hasFeature = (features: string[], ...required: string[]) => {
  return required.some((feature) => features.includes(feature))
}

const isExportDataset = (value: string): value is AnalyticsExportDataset => (
  EXPORT_DATASETS.includes(value as AnalyticsExportDataset)
)

const shortActorId = (value: string): string => {
  if (!value) return '—'
  return value.length > 12 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value
}

const shortCampaignId = (value: string): string => {
  if (!value) return '—'
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value
}

const formatActivityActorType = (event: ActivityRecord): string => event.actorType || 'unknown'

const formatActivityIds = (
  event: ActivityRecord,
  fallbackCampaignId = '',
  campaignNameLookup: (id: string) => string | undefined = () => undefined,
): string => {
  // Prefer human-readable: actor name → email → "User …abc1234"; campaign name
  // → short campaign id. The actor type column already shows "user/system/etc"
  // so we don't repeat it here.
  const cid = event.campaignId || fallbackCampaignId
  const actorLine = event.actorName
    || event.actorEmail
    || (event.actorId ? `User ${event.actorId.slice(-7)}` : '')
  const campaignLine = (cid && campaignNameLookup(cid)) || shortCampaignId(cid)
  return [actorLine, campaignLine].filter(Boolean).join('\n')
}

const summaryMetrics = (summary: AnalyticsScopeSummary): SummaryMetric[] => {
  const used = new Set<string>()
  const prioritized = SUMMARY_PRIORITY
    .filter(({ key }) => typeof summary[key] === 'number')
    .map(({ key, label }) => {
      used.add(key)
      return {
        label,
        value: formatNumber(readNumber(summary[key])),
      }
    })

  const dynamic = Object.entries(summary)
    .filter(([key, value]) => !used.has(key) && !SUMMARY_BLOCKLIST.has(key) && typeof value === 'number')
    .slice(0, Math.max(0, 4 - prioritized.length))
    .map(([key, value]) => ({
      label: toSentenceCase(key),
      value: formatNumber(readNumber(value)),
    }))

  return [...prioritized, ...dynamic].slice(0, 4)
}

export default defineComponent({
  name: 'AnalyticsView',
  components: { Line, Bar, SurveyResponsesModal, AnalyticsRangePicker },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()

    const isLoading = ref(true)
    const loadError = ref('')
    const campaigns = ref<AnalyticsCampaign[]>([])
    const orgDetails = ref<OrgDetails | null>(null)
    const campaignSummary = ref<AnalyticsScopeSummary>({})
    const tenantSummary = ref<AnalyticsScopeSummary>({})
    const orgSummary = ref<AnalyticsScopeSummary>({})
    const leaderboards = ref<Record<string, AnalyticsLeaderboardEntry[]>>({})
    const recentActivity = ref<ActivityRecord[]>([])
    const lastUpdatedAt = ref('')
    const selectedJourneyId = ref('')
    const selectedSessionId = ref('')
    const selectedJourneyTallies = ref<AnalyticsScopeSummary>({})
    const selectedSessionTallies = ref<AnalyticsScopeSummary>({})
    const campaignJourneySummaries = ref<AnalyticsRelatedSummary[]>([])
    const campaignSessionSummaries = ref<AnalyticsRelatedSummary[]>([])
    const selectedJourneyActivity = ref<ActivityRecord[]>([])
    const selectedSessionActivity = ref<ActivityRecord[]>([])
    const historicalGeoFallback = ref<HistoricalAnalyticsQuery | null>(null)
    const historicalPreview = ref<HistoricalAnalyticsQuery | null>(null)
    const chartWindow = ref<ChartWindowId>('24h')
    const chartSeries = ref<HistoricalAnalyticsQuery | null>(null)
    const chartLoading = ref(false)
    const chartError = ref<string | null>(null)
    const overTimeRange = ref<OverTimeRangeId>('7d')
    const overTimeSeries = ref<HistoricalAnalyticsQuery | null>(null)
    const overTimeLoading = ref(false)
    const overTimeError = ref<string | null>(null)

    // Unified analytics range — drives BOTH the Basic over-time charts and
    // the Advanced activity charts. Replaces the two separate chip rows
    // (chartWindow + overTimeRange) with one OS-style picker. The legacy
    // refs are kept above only for any in-flight downstream readers; new
    // code should bind to analyticsRange.
    // Must mirror AnalyticsRangePicker's PRESETS entry for "Last 7 days"
    // (days + granularity + label). The picker's isPresetActive compares
    // all three fields — if any drifts, the chip won't highlight on first
    // paint and the initial query lands at a different granularity than
    // re-picking the same chip would produce.
    const analyticsRange = ref<RangeSpec>({
      kind: 'preset',
      days: 7,
      granularity: 'day',
      label: 'Last 7 days',
    })

    const dataSpan = ref<{
      firstEventTs: string | null
      lastEventTs: string | null
      totalObjects: number
      truncated: boolean
    } | null>(null)

    const analyticsRangeLabel = computed(() => {
      const range = analyticsRange.value
      if (range.kind === 'preset' || range.kind === 'all') return range.label
      const fmt: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
      return `${new Date(range.from).toLocaleString(undefined, fmt)} → ${new Date(range.to).toLocaleString(undefined, fmt)}`
    })

    // Preset windows are calendar-aligned to UTC midnight boundaries so the
    // chart x-axis renders a clean grid edge-to-edge regardless of when in
    // the day the user opens the page. "Last 7 days" = [seven calendar
    // days ending today]; right edge sits at tomorrow 00:00 UTC so today's
    // partial bucket has its slot reserved.
    const computePresetWindow = (days: number, granularity: 'minute' | 'hour' | 'day' | 'week') => {
      const now = new Date()
      if (granularity === 'minute' || granularity === 'hour') {
        // Sub-day windows are still "last N hours up to top-of-hour now".
        // For 24h: right = next top-of-hour, left = right - 24 hours so
        // the canvas always shows exactly 24 hourly slots.
        const toMs = Math.ceil(now.getTime() / (60 * 60 * 1000)) * (60 * 60 * 1000)
        const fromMs = toMs - Math.max(1, Math.ceil(days * 24)) * 60 * 60 * 1000
        return { fromMs, toMs }
      }
      // Daily/weekly: snap right to next UTC midnight, left to N days before.
      const todayUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      const toMs = todayUtcMidnight + 24 * 60 * 60 * 1000
      const fromMs = toMs - Math.max(1, Math.ceil(days)) * 24 * 60 * 60 * 1000
      return { fromMs, toMs }
    }

    const analyticsRangeQuery = computed<{ days?: number; from?: string; to?: string; granularity?: 'minute' | 'hour' | 'day' | 'week' }>(() => {
      const range = analyticsRange.value
      if (range.kind === 'absolute') {
        return { from: range.from, to: range.to, granularity: range.granularity }
      }
      if (range.kind === 'all') {
        // 'All data' bridges from the earliest event we know about to now.
        const from = dataSpan.value?.firstEventTs || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString()
        return { from, to: new Date().toISOString(), granularity: range.granularity }
      }
      const { fromMs, toMs } = computePresetWindow(range.days, range.granularity)
      return {
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
        granularity: range.granularity,
      }
    })

    // Stable scaffold of the calendar-aligned window — independent of what
    // the BE returns. Drives the chart x-axis so the canvas always spans
    // the full picker window, with the data series merged in.
    const analyticsRangeWindow = computed<{ fromMs: number; toMs: number; granularity: 'minute' | 'hour' | 'day' | 'week' } | null>(() => {
      const range = analyticsRange.value
      if (range.kind === 'preset') {
        const { fromMs, toMs } = computePresetWindow(range.days, range.granularity)
        return { fromMs, toMs, granularity: range.granularity }
      }
      if (range.kind === 'absolute') {
        const fromMs = Date.parse(range.from)
        const toMs = Date.parse(range.to)
        if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null
        return { fromMs, toMs, granularity: range.granularity }
      }
      return null
    })
    const downloadEntitlement = ref<{
      campaignId: string
      purchased: boolean
      packsOwned: number
      userCapTotal: number
      claimedSessionCount: number
      canPurchase: boolean
    } | null>(null)
    const downloadEntitlementLoading = ref(false)
    const downloadBusy = ref(false)
    const downloadError = ref<string | null>(null)
    const paywallModal = ref<null | 'confirm-purchase' | 'overflow'>(null)
    const showActivityModal = ref(false)
    watch(showActivityModal, (open) => {
      if (typeof document === 'undefined') return
      document.body.style.overflow = open ? 'hidden' : ''
      document.documentElement.style.overflow = open ? 'hidden' : ''
    })
    const paywallExtraPacks = ref(0)
    const exportJobs = ref<AnalyticsExportJob[]>([])
    let exportJobsTimer: number | null = null

    const requestedCampaignId = computed(() => (
      typeof route.query.campaignId === 'string' && route.query.campaignId.length > 0
        ? route.query.campaignId
        : ''
    ))
    const SIMULATOR_GEO_STORAGE_KEY = 'analytics-simulator-geo'
    const AUTO_REFRESH_MS = 15000
    let analyticsRefreshTimer: number | null = null

    const analyticsMode = computed<AnalyticsMode>(() => (
      route.query.mode === 'advanced' ? 'advanced' : 'standard'
    ))

    const activeSection = computed<AdvancedSection>(() => {
      const requested = typeof route.query.section === 'string' ? route.query.section : ''
      return ADVANCED_SECTIONS.some((section) => section.id === requested)
        ? requested as AdvancedSection
        : 'overview'
    })

    const focusCampaign = computed(() => {
      if (!campaigns.value.length) return null

      if (requestedCampaignId.value) {
        const requested = campaigns.value.find((campaign) => campaign.id === requestedCampaignId.value)
        if (requested) return requested
      }

      return [...campaigns.value].sort((left, right) => right.metrics.registered - left.metrics.registered)[0] || campaigns.value[0]
    })

    // Board row reads the LIVE campaign-scope tally (campaignSummary), not the
    // batch-metrics rollup. The batch lags for same-day campaigns and rendered
    // 0 registrations / 0 for a campaign played today (Geo Count Test), while
    // the live tally already holds the true counts. Registrations = succeeded;
    // Gameplays = experience.started (replaces the old "Claimed" column).
    const focusBoardMetrics = computed(() => ({
      registered: readCount(campaignSummary.value, 'user.registration.succeeded', 'registered')
        || (focusCampaign.value?.metrics.registered ?? 0),
      gameplays: readCount(campaignSummary.value, 'experience.started', 'experience-started'),
    }))

    const lookupCampaignName = (id: string): string | undefined => {
      if (!id) return undefined
      return campaigns.value.find((campaign) => campaign.id === id)?.name
    }

    const loadChartSeries = async () => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) {
        chartSeries.value = null
        return
      }
      chartLoading.value = true
      chartError.value = null
      try {
        const response = await analyticsService.queryHistorical({
          campaignId,
          ...analyticsRangeQuery.value,
          maxObjects: 200,
        })
        chartSeries.value = response
        if (response.dataSpan) {
          dataSpan.value = response.dataSpan
        }
      } catch (err) {
        chartError.value = err instanceof Error ? err.message : 'Failed to load charts'
        chartSeries.value = null
      } finally {
        chartLoading.value = false
      }
    }

    const loadOverTime = async () => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) {
        overTimeSeries.value = null
        return
      }
      overTimeLoading.value = true
      overTimeError.value = null
      try {
        overTimeSeries.value = await analyticsService.queryHistorical({
          campaignId,
          ...analyticsRangeQuery.value,
          maxObjects: 400,
        })
      } catch (err) {
        overTimeError.value = err instanceof Error ? err.message : 'Failed to load analytics'
        overTimeSeries.value = null
      } finally {
        overTimeLoading.value = false
      }
    }

    watch([analyticsRange, () => focusCampaign.value?.id], () => {
      void loadChartSeries()
      void loadOverTime()
    }, { immediate: true })

    const overTimeRangeOptions = OVER_TIME_RANGES
    const overTimeRangeLabel = analyticsRangeLabel

    // Build a list of calendar-aligned bucket keys spanning the window.
    // Bucket keys match the backend's bucketKey() output shape so the
    // merge against the BE response is a plain hash lookup.
    type ScaffoldBucket = {
      ts: number
      key: string
      registrations: number
      gameplays: number
      gameplaysCompleted: number
      prizesAllocated: number
      quizzesCompleted: number
      claims: number
      redeems: number
      shares: number
      uniqueActors: number
      geoPoints: number
    }
    const buildBucketScaffold = (
      fromMs: number,
      toMs: number,
      granularity: 'minute' | 'hour' | 'day' | 'week'
    ): ScaffoldBucket[] => {
      const stepMs = granularity === 'minute' ? 60 * 1000
        : granularity === 'hour' ? 60 * 60 * 1000
        : granularity === 'day' ? 24 * 60 * 60 * 1000
        : 7 * 24 * 60 * 60 * 1000
      const bucketKeyFor = (ts: number): string => {
        const iso = new Date(ts).toISOString()
        if (granularity === 'minute') return iso.slice(0, 16)
        if (granularity === 'hour') return iso.slice(0, 13)
        return iso.slice(0, 10)
      }
      const out: ScaffoldBucket[] = []
      // Cap defensively so a misconfigured window doesn't explode the heap.
      const maxBuckets = 400
      for (let ts = fromMs; ts < toMs && out.length < maxBuckets; ts += stepMs) {
        out.push({
          ts,
          key: bucketKeyFor(ts),
          registrations: 0,
          gameplays: 0,
          gameplaysCompleted: 0,
          prizesAllocated: 0,
          quizzesCompleted: 0,
          claims: 0,
          redeems: 0,
          shares: 0,
          uniqueActors: 0,
          geoPoints: 0,
        })
      }
      return out
    }

    // Merge a BE response series into the scaffold. Missing slots stay
    // zero-filled — that's the point.
    const mergeIntoScaffold = (
      scaffold: ScaffoldBucket[],
      series: Array<{ date: string; [k: string]: unknown }> | undefined
    ): ScaffoldBucket[] => {
      if (!series || !series.length) return scaffold
      const index = new Map(scaffold.map((b) => [b.key, b]))
      for (const point of series) {
        const slot = index.get(point.date)
        if (!slot) continue
        const num = (v: unknown) => typeof v === 'number' ? v : Number(v) || 0
        slot.registrations = num(point.registrations)
        slot.gameplays = num(point.gameplays)
        slot.gameplaysCompleted = num(point.gameplaysCompleted)
        slot.prizesAllocated = num(point.prizesAllocated)
        slot.quizzesCompleted = num(point.quizzesCompleted)
        slot.claims = num(point.claims)
        slot.redeems = num(point.redeems)
        slot.shares = num(point.shares)
        slot.uniqueActors = num(point.uniqueActors)
        slot.geoPoints = num(point.geoPoints)
      }
      return scaffold
    }

    const overTimePaddedSeries = computed<ScaffoldBucket[]>(() => {
      const win = analyticsRangeWindow.value
      if (!win) return []
      const scaffold = buildBucketScaffold(win.fromMs, win.toMs, win.granularity)
      return mergeIntoScaffold(scaffold, overTimeSeries.value?.series as Array<{ date: string }> | undefined)
    })

    const chartPaddedSeries = computed<ScaffoldBucket[]>(() => {
      const win = analyticsRangeWindow.value
      if (!win) return []
      const scaffold = buildBucketScaffold(win.fromMs, win.toMs, win.granularity)
      return mergeIntoScaffold(scaffold, chartSeries.value?.series as Array<{ date: string }> | undefined)
    })

    // Smart x-axis label formatter — picks a human-readable shape per
    // granularity, and adds a day prefix only when the visible window
    // crosses a day boundary so single-day views stay terse ("08:00")
    // and multi-day views stay unambiguous ("22 May 08:00").
    const formatBucketLabel = (isoDate: string, granularity: string, spansMultipleDays: boolean): string => {
      if (!isoDate) return ''
      // BE bucket keys are ISO-prefixes (YYYY-MM-DD, YYYY-MM-DDTHH, etc.)
      // — pad to a parseable shape before constructing the Date.
      const iso = isoDate.length === 10 ? `${isoDate}T00:00:00Z`
        : isoDate.length === 13 ? `${isoDate}:00:00Z`
        : isoDate.length === 16 ? `${isoDate}:00Z`
        : isoDate
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return isoDate
      if (granularity === 'minute' || granularity === 'hour') {
        const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
        if (spansMultipleDays) {
          const day = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
          return `${day} ${time}`
        }
        return time
      }
      // day / week granularity
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
    }

    const seriesSpansMultipleDays = (series: Array<{ date: string }>): boolean => {
      if (series.length < 2) return false
      const firstDay = series[0]?.date?.slice(0, 10)
      const lastDay = series[series.length - 1]?.date?.slice(0, 10)
      return Boolean(firstDay && lastDay && firstDay !== lastDay)
    }

    const overTimeLabels = computed(() => {
      const series = overTimePaddedSeries.value
      const granularity = analyticsRangeWindow.value?.granularity
        ?? overTimeSeries.value?.window?.granularity
        ?? 'day'
      const multiDay = (analyticsRangeWindow.value
        ? (analyticsRangeWindow.value.toMs - analyticsRangeWindow.value.fromMs) > 24 * 60 * 60 * 1000
        : seriesSpansMultipleDays(series.map((b) => ({ date: b.key }))))
      return series.map((b) => formatBucketLabel(b.key, granularity, multiDay))
    })

    const overTimeRegistrationsChart = computed(() => ({
      labels: overTimeLabels.value,
      datasets: [{
        label: 'Registrations',
        data: overTimePaddedSeries.value.map((b) => b.registrations),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.2)',
        fill: true,
        tension: 0.3,
      }],
    }))

    const overTimeGameplaysChart = computed(() => ({
      labels: overTimeLabels.value,
      datasets: [{
        label: 'Gameplays',
        data: overTimePaddedSeries.value.map((b) => b.gameplays),
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.2)',
        fill: true,
        tension: 0.3,
      }],
    }))

    const overTimePrizesAllocatedChart = computed(() => ({
      labels: overTimeLabels.value,
      datasets: [{
        label: 'Prizes Won',
        data: overTimePaddedSeries.value.map((b) => b.prizesAllocated),
        borderColor: 'rgb(244, 114, 182)',
        backgroundColor: 'rgba(244, 114, 182, 0.2)',
        fill: true,
        tension: 0.3,
      }],
    }))

    const overTimeTotals = computed(() => {
      // All three headline counts source from the same S3 historical
      // familyTotals so the picker window is the single source of truth.
      // Previously gameplays + prizesWon read from Valkey (live counter)
      // while registrations read from S3 — leading to a confusing divergence
      // where e.g. the card said 3 (Valkey lifetime) but the chart spike sum
      // showed 6 (S3 within the picker window). Now: card = chart, always.
      //
      // We lose the "live" pop on gameplays/prizes for very recent activity
      // that hasn't landed in the lake yet, but gain internal consistency.
      // Live counters still surface in the Live Ratios panel below if a
      // user needs the up-to-the-second view.
      const totals = overTimeSeries.value?.familyTotals
      return {
        registrations: totals?.registrations ?? 0,
        gameplays: totals?.gameplays ?? 0,
        prizesWon: totals?.prizesAllocated ?? 0,
      }
    })

    const chartLabels = computed(() => {
      const series = chartPaddedSeries.value
      const granularity = analyticsRangeWindow.value?.granularity
        ?? chartSeries.value?.window?.granularity
        ?? 'day'
      const multiDay = (analyticsRangeWindow.value
        ? (analyticsRangeWindow.value.toMs - analyticsRangeWindow.value.fromMs) > 24 * 60 * 60 * 1000
        : seriesSpansMultipleDays(series.map((b) => ({ date: b.key }))))
      return series.map((b) => formatBucketLabel(b.key, granularity, multiDay))
    })

    // Funnel totals: drop categories whose count is zero so an unwired
    // metric (e.g. Shares, before the share pipeline ships) doesn't leave
    // an empty bar staring at the operator. As soon as a count appears, the
    // row re-appears automatically.
    const funnelChart = computed(() => {
      const totals = chartSeries.value?.familyTotals
      const rows = [
        { label: 'Registrations', value: totals?.registrations ?? 0, colour: 'rgba(99, 102, 241, 0.75)' },
        { label: 'Gameplays', value: totals?.gameplays ?? 0, colour: 'rgba(167, 139, 250, 0.75)' },
        { label: 'Shares', value: totals?.shares ?? 0, colour: 'rgba(236, 72, 153, 0.75)' },
      ].filter((r) => r.value > 0)
      if (rows.length === 0) {
        // Preserve the all-zero placeholder so the chart still renders an
        // axis frame rather than collapsing to nothing.
        return {
          labels: ['Registrations', 'Gameplays'],
          datasets: [{ label: 'Total people', data: [0, 0], backgroundColor: ['rgba(99, 102, 241, 0.75)', 'rgba(167, 139, 250, 0.75)'] }],
        }
      }
      return {
        labels: rows.map((r) => r.label),
        datasets: [{
          label: 'Total people',
          data: rows.map((r) => r.value),
          backgroundColor: rows.map((r) => r.colour),
        }],
      }
    })

    // Quiz drop-off: per-stage answered counts from
    // experience.stageCompleted events. Q1 = everyone who reached it
    // and answered, declining bar by bar as users quit mid-quiz.
    // Falls back to a simple Registered→Completed funnel when no
    // per-stage data has arrived yet (backend pre-deploy).
    //
    // KNOWN ISSUE (IRL2-2876 follow-up): the backend currently emits
    // stageCompleted events that may all share the same stageOrder=1
    // for multi-question quizzes, which collapses Q1/Q2/Q3 into a
    // single bar. Real per-question buckets need the emitter to
    // populate questionOrder distinctly. Tracked separately — this
    // chart will render correctly the moment the emitter is fixed.
    const quizDropOffChart = computed(() => {
      const stages = chartSeries.value?.quizDropOff ?? []
      if (stages.length > 0) {
        return {
          labels: stages.map((s) => `Q${s.stage}`),
          datasets: [{
            label: 'Players who answered',
            data: stages.map((s) => s.count),
            backgroundColor: 'rgba(99, 102, 241, 0.75)',
          }],
        }
      }
      const totals = chartSeries.value?.familyTotals
      return {
        labels: ['Registered', 'Quizzes completed'],
        datasets: [{
          label: 'Players',
          data: totals ? [totals.registrations, totals.quizzesCompleted] : [0, 0],
          backgroundColor: ['rgba(99, 102, 241, 0.75)', 'rgba(16, 185, 129, 0.75)'],
        }],
      }
    })

    // Quiz leaderboard: prefer the cache-manager player profile data
    // (handle = real player_name with anonymized fallback, bestScore
    // + lastScore per-player in the current campaign). Falls back to
    // the historical-events chartSeries.quizLeaderboard (cumulative
    // score across the window, anonymous UUIDs trimmed for readability)
    // only when the cache-manager Quiz board hasn't loaded yet — so the
    // chart never goes blank during cold-start.
    const quizLeaderboardChart = computed(() => {
      const cacheBoard = leaderboards.value['Quiz'] ?? []
      if (cacheBoard.length > 0) {
        return {
          labels: cacheBoard.map((entry, idx) => `#${idx + 1} ${entry.handle || entry.actorId.slice(0, 8)}`),
          datasets: [{
            label: 'Best score (Last in tooltip)',
            data: cacheBoard.map((entry) => entry.bestScore ?? entry.score),
            backgroundColor: 'rgba(245, 158, 11, 0.75)',
            // Stash lastScore on the dataset so the tooltip callback can
            // reach it without a parallel array.
            lastScores: cacheBoard.map((entry) => entry.lastScore ?? entry.score),
          }],
        }
      }
      const board = chartSeries.value?.quizLeaderboard ?? []
      return {
        labels: board.map((entry, idx) => `#${idx + 1} ${entry.actorId.slice(0, 8)}`),
        datasets: [{
          label: 'Score',
          data: board.map((entry) => entry.score),
          backgroundColor: 'rgba(245, 158, 11, 0.75)',
        }],
      }
    })

    // Custom horizontal-bar options for the Quiz leaderboard so the
    // tooltip shows BOTH best and last side by side.
    const quizLeaderboardChartOptions = computed(() => ({
      ...horizontalBarOptions.value,
      plugins: {
        ...horizontalBarOptions.value.plugins,
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const best = ctx.parsed?.x ?? ctx.parsed?.y ?? 0
              const lastArr = ctx.dataset?.lastScores as number[] | undefined
              const last = lastArr ? lastArr[ctx.dataIndex] : null
              return last != null && last !== best
                ? `Best ${best}  ·  Last ${last}`
                : `Best ${best}`
            },
          },
        },
      },
    }))

    // Conversion rate (Pete's billing-aligned definition): how many
    // gameplay sessions each registrant has produced over the campaign's
    // life, sampled cumulatively at each point in time.
    //
    //   y = cumulative gameplays so far / cumulative registrations so far
    //
    // 1.0 = every registrant played once and never returned.
    // >1.0 = users coming back for multiple sessions.
    // <1.0 = registrants who haven't played yet (uncommon — would mean
    // the register event fires before the first activity window).
    const conversionChart = computed(() => {
      const series = chartPaddedSeries.value
      let cumGameplays = 0
      let cumRegistrations = 0
      const data = series.map((p) => {
        cumGameplays += p.gameplays
        cumRegistrations += p.registrations
        return cumRegistrations > 0
          ? Number((cumGameplays / cumRegistrations).toFixed(2))
          : 0
      })
      return {
        labels: chartLabels.value,
        datasets: [
          {
            label: 'Gameplays per registrant',
            data,
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            tension: 0.3,
            fill: true,
          },
        ],
      }
    })

    // True conversion rate: % of registrants who have completed at least
    // one experience as of this point in time. Bounded [0, 100].
    //
    // Computed as: min(cumulative gameplaysCompleted, cumulative
    // registrations) / cumulative registrations * 100. The min() saturates
    // the numerator so a single user completing repeatedly can't push the
    // ratio past 100% — capture-the-funnel semantics rather than play-
    // frequency semantics.
    //
    // Caveat: this is an upper-bound approximation because the BE returns
    // aggregate counts, not per-actor "has completed" data. A user
    // completing twice while a second user never completes would still
    // read as 100% here. Acceptable for billing-grade dashboarding;
    // exact-per-user reporting needs a separate backend roll-up.
    const conversionRateChart = computed(() => {
      const series = chartPaddedSeries.value
      let cumRegistrations = 0
      let cumCompleted = 0
      const data = series.map((p) => {
        cumRegistrations += p.registrations
        cumCompleted += p.gameplaysCompleted
        if (cumRegistrations === 0) return 0
        const cappedCompleted = Math.min(cumCompleted, cumRegistrations)
        return Number(((cappedCompleted / cumRegistrations) * 100).toFixed(1))
      })
      return {
        labels: chartLabels.value,
        datasets: [
          {
            label: '% of registrants completing an experience',
            data,
            borderColor: 'rgb(56, 189, 248)',
            backgroundColor: 'rgba(56, 189, 248, 0.18)',
            tension: 0.3,
            fill: true,
          },
        ],
      }
    })

    // Survey "Top answer per question": for each survey question, plot the
    // single most-picked answer as a horizontal bar. Source data is the
    // existing answerSummary endpoint which already aggregates per-question
    // top-5 answer counts. We surface just the top-1 here for a compact
    // card; the full per-question breakdown remains in the campaign-insights
    // section deeper on the page.
    const surveyTopAnswerChart = computed(() => {
      const questions = answerSummary.value?.questions ?? []
      const rows = questions
        .filter((q) => q.topAnswers.length > 0)
        .map((q) => ({
          label: `Q${q.questionOrder}: ${q.topAnswers[0].value.slice(0, 30)}`,
          count: q.topAnswers[0].count,
        }))
      return {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: 'Top answer (count)',
            data: rows.map((r) => r.count),
            backgroundColor: 'rgba(168, 85, 247, 0.7)',
            borderColor: 'rgb(168, 85, 247)',
            borderWidth: 1,
          },
        ],
      }
    })

    // AR engagement funnel: four-step stacked-style bar showing how many
    // journeys arrived, started the experience, interacted (any stage), and
    // ended. Numerator = unique journeys at each step within the window.
    // Computed entirely from existing series + topEvents — no extra fetch.
    const arFunnelChart = computed(() => {
      const totals = chartSeries.value?.familyTotals
      const arrivals = totals?.gameplays ?? 0
      const completed = totals?.gameplaysCompleted ?? 0
      const topEvents = chartSeries.value?.topEvents ?? []
      const started = topEvents.find((e) => e.label === 'experience.started')?.count ?? 0
      const interacted = topEvents.find((e) => e.label === 'experience.stageCompleted')?.count ?? 0
      return {
        labels: ['Arrived', 'Started', 'Interacted', 'Ended'],
        datasets: [
          {
            label: 'Journeys',
            data: [arrivals, started, interacted, completed],
            backgroundColor: [
              'rgba(56, 189, 248, 0.7)',
              'rgba(99, 102, 241, 0.7)',
              'rgba(168, 85, 247, 0.7)',
              'rgba(16, 185, 129, 0.7)',
            ],
            borderWidth: 1,
          },
        ],
      }
    })

    // AR session duration histogram: bucketed engagement time pulled from
    // experience.ended.durationSeconds. Helps the operator distinguish
    // instant-bounce sessions from real engagement.
    const arDurationChart = computed(() => {
      const buckets = chartSeries.value?.durationBuckets ?? []
      return {
        labels: buckets.map((b) => b.label),
        datasets: [
          {
            label: 'Sessions',
            data: buckets.map((b) => b.count),
            backgroundColor: 'rgba(245, 158, 11, 0.7)',
            borderColor: 'rgb(245, 158, 11)',
            borderWidth: 1,
          },
        ],
      }
    })

    // Geolocation: per-city counts when the API surfaces `geoCities`
    // (query-historical-analytics aggregates `geo.city` from event
    // records). Falls back to time-bucket geo-tagged event counts when
    // the backend hasn't deployed the city aggregation yet, so the
    // chart never goes blank during the FE/BE deploy window.
    const geoPointsChart = computed(() => {
      const cities = chartSeries.value?.geoCities ?? []
      if (cities.length > 0) {
        return {
          labels: cities.map((c) => c.label),
          datasets: [
            {
              label: 'Geo-tagged events',
              data: cities.map((c) => c.count),
              backgroundColor: 'rgba(236, 72, 153, 0.6)',
              borderColor: 'rgb(236, 72, 153)',
              borderWidth: 1,
            },
          ],
        }
      }
      const series = chartPaddedSeries.value
      return {
        labels: chartLabels.value,
        datasets: [
          {
            label: 'Geo-tagged events',
            data: series.map((p) => p.geoPoints ?? 0),
            backgroundColor: 'rgba(236, 72, 153, 0.6)',
            borderColor: 'rgb(236, 72, 153)',
            borderWidth: 1,
          },
        ],
      }
    })

    const chartCommonOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const, labels: { color: '#e2e8f0' } },
        tooltip: { mode: 'index' as const, intersect: false },
      },
      scales: {
        x: {
          // Render a tick at every bucket boundary (24 for 24h, 7 for 7d,
          // etc.) so the structure is visible, but only label the first
          // and last positions to keep the axis legible. autoSkip is off
          // because we *want* every tick mark drawn — the callback handles
          // label decluttering directly.
          ticks: {
            color: '#cbd5e1',
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            callback(this: { getLabelForValue: (v: number) => string }, _value: string | number, index: number, ticks: Array<unknown>): string {
              if (index === 0 || index === ticks.length - 1) {
                return this.getLabelForValue(index)
              }
              return ''
            },
          },
          grid: { color: 'rgba(255,255,255,0.05)' },
        },
        y: { beginAtZero: true, ticks: { color: '#cbd5e1' }, grid: { color: 'rgba(255,255,255,0.08)' } },
      },
    }))

    const stackedBarOptions = computed(() => ({
      ...chartCommonOptions.value,
      scales: {
        x: { ...chartCommonOptions.value.scales.x, stacked: true },
        y: { ...chartCommonOptions.value.scales.y, stacked: true },
      },
    }))

    // Y-axis clamped to 0–100 for percentage metrics like Conversion Rate.
    const percentageChartOptions = computed(() => ({
      ...chartCommonOptions.value,
      scales: {
        x: chartCommonOptions.value.scales.x,
        y: {
          ...chartCommonOptions.value.scales.y,
          min: 0,
          max: 100,
          ticks: { ...chartCommonOptions.value.scales.y.ticks, callback: (v: number | string) => `${v}%` },
        },
      },
    }))

    const horizontalBarOptions = computed(() => ({
      ...chartCommonOptions.value,
      indexAxis: 'y' as const,
      plugins: { ...chartCommonOptions.value.plugins, legend: { display: false } },
    }))

    const chartWindowOptions = CHART_WINDOWS

    // Campaign-type detection. experienceType can be 'quiz' | 'survey'
    // (lowercase), or one of the legacy human-readable strings like
    // 'Augmented Reality' / 'Augmented Reality AND Map' / 'Map'. Rather
    // than match every variant, normalise to a small set of kinds the
    // dashboard cares about. 'other' is a safe fallback — no type-specific
    // sections render for it.
    const campaignKind = computed<'quiz' | 'survey' | 'ar' | 'map' | 'other'>(() => {
      const raw = focusCampaign.value?.experienceType
      if (!raw) return 'other'
      const lower = raw.toLowerCase().trim()
      if (lower.includes('quiz')) return 'quiz'
      if (lower.includes('survey')) return 'survey'
      if (lower === 'ar' || lower.includes('augmented')) return 'ar'
      if (lower === 'map' || lower === 'geo') return 'map'
      return 'other'
    })

    const campaignHasGeoPoints = computed(() => (chartSeries.value?.totals?.geoPoints ?? 0) > 0)

    // Boundary axis (orthogonal to experienceType). 'geo' campaigns are
    // played inside a geo-fence and emit geo events; 'instant' campaigns
    // are played anywhere and have no geo signal by design. Null = legacy
    // campaigns created before the field was persisted — treat as 'instant'
    // so geo widgets stay hidden by default rather than rendering empty.
    const campaignBoundary = computed<'instant' | 'geo'>(() => {
      const raw = focusCampaign.value?.boundary
      return raw === 'geo' ? 'geo' : 'instant'
    })
    const showGeoSurfaces = computed(() => campaignBoundary.value === 'geo')

    const advancedChartsSummary = computed(() => {
      const base = 'Funnel, Gameplays per registrant, Conversion rate'
      const geoSuffix = showGeoSurfaces.value ? ', Geolocation' : ''
      if (campaignKind.value === 'survey') {
        return `${base}, Response rate per question, Top answer per question${geoSuffix}`
      }
      if (campaignKind.value === 'ar') {
        return `${base}, AR engagement funnel, Session duration${geoSuffix}`
      }
      if (campaignKind.value === 'quiz') {
        return `${base}, Quiz drop-off, Quiz leaderboard${geoSuffix}`
      }
      return `${base}${geoSuffix}`
    })

    // Advanced section nav filtered by boundary — instant campaigns don't
    // get a 'Geo' tab. Consumers iterate this rather than ADVANCED_SECTIONS
    // directly so new boundary-gated sections only have to be filtered here.
    const visibleAdvancedSections = computed(() =>
      ADVANCED_SECTIONS.filter((section) => section.id !== 'geo' || showGeoSurfaces.value)
    )

    // Collapse state per type-aware section. Collapsed by default so the
    // tiles don't fetch data (or mount heavy components) until the user
    // asks to see them — cheap, bounded, neat per repo policy.
    const geoSectionOpen = ref(false)
    const quizSectionOpen = ref(false)
    const surveySectionOpen = ref(false)
    const arSectionOpen = ref(false)

    type AnswerSummary = {
      totalRespondents: number
      totalSessionsDetected: number
      truncated: boolean
      questions: Array<{
        questionId: string
        responseType: string
        questionOrder: number
        totalResponses: number
        topAnswers: Array<{ value: string; count: number }>
      }>
    }
    const answerSummary = ref<AnswerSummary | null>(null)
    const answerSummaryLoading = ref(false)
    const answerSummaryError = ref<string | null>(null)
    const answerSummaryCampaignId = ref<string>('')
    const responsesModalOpen = ref(false)
    const includeAnswersInCsv = ref(false)

    const loadAnswerSummary = async () => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) return
      // Cache hit: already have the summary for this campaign — don't refetch.
      if (answerSummaryCampaignId.value === campaignId && answerSummary.value) return
      answerSummaryLoading.value = true
      answerSummaryError.value = null
      try {
        const summary = await analyticsService.getCampaignAnswerSummary(campaignId, 5)
        answerSummary.value = summary
        answerSummaryCampaignId.value = campaignId
      } catch (err) {
        answerSummaryError.value = err instanceof Error ? err.message : 'Failed to load answer summary'
        answerSummary.value = null
      } finally {
        answerSummaryLoading.value = false
      }
    }

    // Lazy fetch: only when the quiz or survey tile opens, and only once per
    // campaign. Closing + reopening the tile won't re-hit the backend.
    watch([quizSectionOpen, surveySectionOpen], ([quizOpen, surveyOpen]) => {
      if (quizOpen || surveyOpen) void loadAnswerSummary()
    })
    // Clear cached summary when the user switches campaigns, and immediately
    // prefetch for quiz / survey campaigns so the Top Answer Per Question chart
    // card up top populates without waiting for the operator to expand the
    // Survey-answers tile below (IRL2-2917).
    watch(() => focusCampaign.value?.id, (newId) => {
      if (newId !== answerSummaryCampaignId.value) {
        answerSummary.value = null
        answerSummaryCampaignId.value = ''
        answerSummaryError.value = null
      }
      if (newId && (campaignKind.value === 'quiz' || campaignKind.value === 'survey')) {
        void loadAnswerSummary()
      }
    }, { immediate: true })

    const loadDownloadEntitlement = async () => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) {
        downloadEntitlement.value = null
        return
      }
      downloadEntitlementLoading.value = true
      try {
        downloadEntitlement.value = await analyticsService.getCampaignDownloadEntitlement(campaignId)
      } catch (err) {
        console.error('Failed to load download entitlement', err)
        downloadEntitlement.value = null
      } finally {
        downloadEntitlementLoading.value = false
      }
    }

    const uniqueActorsInChart = computed(() => chartSeries.value?.totals?.uniqueActors ?? 0)
    const packsNeededForAll = computed(() => {
      const users = uniqueActorsInChart.value
      return users > 0 ? Math.ceil(users / 1000) : 1
    })
    const entitlementCoversAll = computed(() => {
      const e = downloadEntitlement.value
      if (!e || !e.purchased) return false
      return e.userCapTotal >= uniqueActorsInChart.value
    })

    const buildRedirectUrls = (campaignId: string) => {
      const successUrl = `${window.location.origin}${window.location.pathname}?campaignId=${encodeURIComponent(campaignId)}`
      const cancelUrl = successUrl
      return { successUrl, cancelUrl }
    }

    const onDownloadClick = () => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) return
      const entitlement = downloadEntitlement.value
      if (!entitlement || !entitlement.purchased) {
        // Free tier and paid-without-pack both land here. The backend
        // handler adds the Professional subscription to the same Stripe
        // session automatically when canPurchase is false.
        paywallExtraPacks.value = packsNeededForAll.value
        paywallModal.value = 'confirm-purchase'
        return
      }
      if (entitlementCoversAll.value) {
        void startCsvDownload('all')
        return
      }
      const shortfall = Math.max(packsNeededForAll.value - entitlement.packsOwned, 1)
      paywallExtraPacks.value = shortfall
      paywallModal.value = 'overflow'
    }

    const confirmPurchasePack = async (quantity: number) => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) return
      downloadBusy.value = true
      downloadError.value = null
      try {
        const { successUrl, cancelUrl } = buildRedirectUrls(campaignId)
        const packs = Math.max(1, Math.min(quantity, 50))
        const { url } = await analyticsService.purchaseCampaignDownloadPack({
          campaignId,
          packQuantity: packs,
          successUrl,
          cancelUrl,
        })
        if (!url) throw new Error('Stripe did not return a checkout URL.')
        window.location.href = url
      } catch (err) {
        downloadError.value = err instanceof Error ? err.message : 'Failed to start checkout'
        downloadBusy.value = false
      }
    }

    const startCsvDownload = async (mode: 'all' | 'latest') => {
      const campaignId = focusCampaign.value?.id
      if (!campaignId) return
      downloadBusy.value = true
      downloadError.value = null
      try {
        const { blob, fileName } = await analyticsService.exportCampaignUsers(campaignId, mode, {
          includeAnswers: includeAnswersInCsv.value,
        })
        const downloadUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(downloadUrl)
        paywallModal.value = null
      } catch (err: any) {
        const serverMessage = err?.response?.data?.error
        downloadError.value = typeof serverMessage === 'string' ? serverMessage
          : err instanceof Error ? err.message : 'Download failed'
      } finally {
        downloadBusy.value = false
      }
    }

    const claimStripeReturn = async () => {
      const sessionId = typeof route.query.session_id === 'string' ? route.query.session_id : ''
      const packType = typeof route.query.pack_type === 'string' ? route.query.pack_type : ''
      const returnedCampaignId = typeof route.query.campaign_id === 'string' ? route.query.campaign_id : ''
      if (!sessionId || packType !== 'campaign-download') return
      const campaignId = returnedCampaignId || focusCampaign.value?.id
      if (!campaignId) return
      try {
        await analyticsService.claimCampaignDownloadPack(campaignId, sessionId)
        await loadDownloadEntitlement()
        router.replace({
          name: route.name || undefined,
          query: { campaignId },
        })
      } catch (err) {
        console.error('Failed to claim download pack', err)
        downloadError.value = err instanceof Error ? err.message : 'Failed to finalise your purchase — please refresh.'
      }
    }

    watch(() => focusCampaign.value?.id, () => {
      void loadDownloadEntitlement()
    }, { immediate: true })

    const analyticsHeading = computed(() => {
      if (focusCampaign.value) {
        return focusCampaign.value.name
      }
      return 'Campaign analytics'
    })

    const campaignScheduleLabel = computed(() => {
      if (!focusCampaign.value) return 'Schedule unavailable'
      const parts = [focusCampaign.value.startDate, focusCampaign.value.endDate]
        .filter((value): value is string => Boolean(value))
        .map((value) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value)))
      return parts.length ? parts.join(' → ') : 'Schedule unavailable'
    })

    const requiresAdvancedAccess = computed(() => analyticsMode.value === 'advanced')
    const hasAdvancedAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.advanced')
    })
    // OpenSearch UI deliberately hidden — prod proxy is flaky (500s on
    // /analytics/tallies in prod since 2026-04-21) and we don't want to
    // surface a feature entry point that fails. The underlying route
    // (DashboardAnalyticsOpenSearch) and openOpenSearchAddon() helper
    // remain intact so a direct URL still works for ops debugging; this
    // gate only controls whether the UI exposes the option. Flip back to
    // the feature check once the prod OpenSearch proxy is stabilised.
    const hasOpenSearchAccess = computed(() => {
      // Keep the feature evaluation warm for observability/future re-enable
      // without leaving dead code behind.
      const features = orgDetails.value?.features || []
      void hasFeature(features, 'analytics.opensearch_addon')
      return false
    })
    const hasJourneyExplorerAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.journey_explorer', 'analytics.advanced')
    })
    const hasDiagnosticsAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.diagnostics', 'analytics.advanced')
    })
    const hasGeoAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.geo', 'analytics.advanced')
    })
    const hasExportsAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.exports', 'analytics.advanced')
    })
    const hasHistoricalExportAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.duckdb', 'analytics.advanced')
    })
    const hasUserPiiAccess = computed(() => {
      const features = orgDetails.value?.features || []
      return hasFeature(features, 'analytics.user_pii')
    })

    const eventMixBars = computed(() => {
      const metrics = focusCampaign.value?.metrics || { registered: 0, claimed: 0, redeemed: 0, shared: 0 }
      const values = [
        { label: 'Registrations', value: metrics.registered },
        { label: 'Claimed', value: metrics.claimed },
      ]
      const maxValue = Math.max(...values.map((item) => item.value), 1)

      return values.map((item) => ({
        ...item,
        width: Math.max((item.value / maxValue) * 100, item.value > 0 ? 8 : 0),
      }))
    })

    const rankedCampaigns = computed(() => (
      [...campaigns.value].sort((left, right) => (
        right.metrics.registered - left.metrics.registered ||
        right.metrics.claimed - left.metrics.claimed
      ))
    ))

    const totalMetrics = computed(() => campaigns.value.reduce((totals, campaign) => ({
      registered: totals.registered + campaign.metrics.registered,
      claimed: totals.claimed + campaign.metrics.claimed,
      redeemed: totals.redeemed + campaign.metrics.redeemed,
      shared: totals.shared + campaign.metrics.shared,
    }), { registered: 0, claimed: 0, redeemed: 0, shared: 0 }))

    const ratioCards = computed(() => {
      const metrics = focusCampaign.value?.metrics || totalMetrics.value
      // Claim rate is a true rate — fraction of registrants who have a
      // claim event, bounded [0, 100%]. Capping with min() saturates the
      // numerator so e.g. 10 claim events across 2 registrants reads as
      // 100% (everyone has claimed) rather than 500%. The detail line
      // surfaces the raw counts so the underlying ratio stays visible
      // even when the headline saturates.
      const cappedClaims = Math.min(metrics.claimed, metrics.registered)
      const claimRate = metrics.registered > 0 ? cappedClaims / metrics.registered : 0

      return [
        {
          label: 'Claim rate',
          value: `${Math.round(claimRate * 100)}%`,
          detail: `${metrics.claimed} claimed from ${metrics.registered} registrations`,
        },
      ]
    })

    const summaryCards = computed(() => {
      const activeCount = campaigns.value.filter((campaign) => campaign.lifecycleStatus === 'Active').length
      const metrics = focusCampaign.value?.metrics || totalMetrics.value
      const campaignLabel = focusCampaign.value ? 'Focused campaign' : 'Campaigns'
      const focusedCampaignName = focusCampaign.value?.name || ''
      const focusedCampaignDisplayName = focusedCampaignName.length > 28
        ? `${focusedCampaignName.slice(0, 28)}...`
        : focusedCampaignName

      return [
        {
          label: campaignLabel,
          value: focusCampaign.value ? focusedCampaignDisplayName : `${campaigns.value.length}`,
          fullValue: focusCampaign.value ? focusedCampaignName : `${campaigns.value.length}`,
          truncateValue: Boolean(focusCampaign.value),
          detail: focusCampaign.value ? `${focusCampaign.value.lifecycleStatus} · ${campaigns.value.length} total campaigns` : `${activeCount} active`,
        },
        {
          label: 'Registrations',
          value: formatNumber(metrics.registered),
          fullValue: formatNumber(metrics.registered),
          truncateValue: false,
          detail: `${formatNumber(Math.max(readNumber(campaignSummary.value['user.registration.succeeded']), readNumber(campaignSummary.value['user-registered'])))} live stream tally`,
        },
        {
          label: 'Claimed',
          value: formatNumber(metrics.claimed),
          fullValue: formatNumber(metrics.claimed),
          truncateValue: false,
          detail: `${formatNumber(readCount(campaignSummary.value, 'reward.drop.claimed', 'claimed'))} current campaign total`,
        },
        {
          label: 'Billable sessions',
          value: formatNumber(readNumber(campaignSummary.value.billableSessions) || readNumber(tenantSummary.value.billableSessions)),
          fullValue: formatNumber(readNumber(campaignSummary.value.billableSessions) || readNumber(tenantSummary.value.billableSessions)),
          truncateValue: false,
          detail: `${formatNumber(readNumber(campaignSummary.value.subscriptionTokensBurned) || readNumber(tenantSummary.value.subscriptionTokensBurned))} tokens burned`,
        },
      ]
    })

    const leaderboardCards = computed(() => (
      Object.entries(leaderboards.value).map(([label, entries]) => ({ label, entries }))
    ))
    const leaderboardRows = computed(() => (
      leaderboardCards.value.flatMap((board) => (
        board.entries.map((entry, index) => ({
          key: `${board.label}-${entry.actorId}-${index}`,
          board: board.label,
          rank: index + 1,
          actorType: entry.actorType,
          actorId: entry.actorId,
          score: entry.score,
        }))
      ))
    ))

    const journeySummaries = computed(() => {
      const fromRelated = buildRelatedEntitySummaries(campaignJourneySummaries.value, 'Journey activity')
      return fromRelated.length ? fromRelated : buildEntitySummaries(recentActivity.value, 'journeyId')
    })
    const sessionSummaries = computed(() => {
      const fromRelated = buildRelatedEntitySummaries(campaignSessionSummaries.value, 'Session activity')
      return fromRelated.length ? fromRelated : buildEntitySummaries(recentActivity.value, 'sessionId')
    })
    const selectedJourneySummary = computed(() => {
      const summaries = journeySummaries.value
      if (!summaries.length) return null
      return summaries.find((journey) => journey.id === selectedJourneyId.value) || summaries[0]
    })
    const selectedJourneyEvents = computed(() => {
      const id = selectedJourneySummary.value?.id
      if (!id) return []
      const scoped = selectedJourneyActivity.value.length
        ? selectedJourneyActivity.value
        : recentActivity.value
      return scoped
        .filter((activity) => activity.journeyId === id)
        .sort(sortByOccurredAtDesc)
    })
    const selectedJourneyMetrics = computed(() => summaryMetrics(selectedJourneyTallies.value))
    const selectedJourneyInsights = computed(() => {
      const events = selectedJourneyEvents.value
      const errorCount = events.filter((activity) => /error|fail|denied|blocked/i.test(activity.label)).length
      const missingRequestCount = events.filter((activity) => !activity.requestId).length
      const geoCount = events.filter((activity) => Boolean(activity.geoPoint)).length
      const uniqueEventCount = new Set(events.map((activity) => activity.label)).size
      const completedCount = events.filter((activity) => isRedeemLabel(activity.label) || isShareLabel(activity.label)).length
      const durationMinutes = events.length > 1
        ? Math.max(
          Math.round(
            (new Date(events[0].occurredAt).getTime() - new Date(events[events.length - 1].occurredAt).getTime()) / 60000
          ),
          0
        )
        : 0

      return [
        { label: 'Signals', value: formatNumber(uniqueEventCount) },
        { label: 'Errors', value: formatNumber(errorCount) },
        { label: 'Missing requests', value: formatNumber(missingRequestCount) },
        { label: 'Geo events', value: formatNumber(geoCount) },
        { label: 'Later-stage events', value: formatNumber(completedCount) },
        { label: 'Duration', value: `${formatNumber(durationMinutes)}m` },
      ]
    })
    const selectedJourneyTransitions = computed(() => {
      const counts = new Map<string, number>()
      const events = [...selectedJourneyEvents.value].sort((left, right) => new Date(left.occurredAt).getTime() - new Date(right.occurredAt).getTime())

      for (let index = 0; index < events.length - 1; index += 1) {
        const current = events[index]
        const next = events[index + 1]
        const label = `${current.label} → ${next.label}`
        counts.set(label, (counts.get(label) || 0) + 1)
      }

      return Array.from(counts.entries())
        .sort((left, right) => right[1] - left[1])
        .map(([label, count]) => ({
          label,
          value: formatNumber(count),
          description: count > 1 ? 'Repeated transition in the current journey timeline.' : 'Observed handoff in this journey timeline.',
        }))
        .slice(0, 6)
    })
    const selectedSessionSummary = computed(() => {
      const summaries = sessionSummaries.value
      if (!summaries.length) return null
      return summaries.find((session) => session.id === selectedSessionId.value) || summaries[0]
    })
    const selectedSessionEvents = computed(() => {
      const id = selectedSessionSummary.value?.id
      if (!id) return []
      const scoped = selectedSessionActivity.value.length
        ? selectedSessionActivity.value
        : recentActivity.value
      return scoped
        .filter((activity) => activity.sessionId === id)
        .sort(sortByOccurredAtDesc)
    })
    const selectedSessionMetrics = computed(() => summaryMetrics(selectedSessionTallies.value))
    const selectedSessionInsights = computed(() => {
      const events = selectedSessionEvents.value
      const uniqueJourneys = new Set(events.map((activity) => activity.journeyId).filter(Boolean)).size
      const errorCount = events.filter((activity) => /error|fail|denied|blocked/i.test(activity.label)).length
      const missingRequestCount = events.filter((activity) => !activity.requestId).length
      const actorCount = new Set(events.map((activity) => `${activity.actorType}:${activity.actorId}`).filter((value) => value !== ':')).size

      return [
        { label: 'Journeys', value: formatNumber(uniqueJourneys) },
        { label: 'Actors', value: formatNumber(actorCount) },
        { label: 'Errors', value: formatNumber(errorCount) },
        { label: 'Missing requests', value: formatNumber(missingRequestCount) },
      ]
    })
    const eventFamilyCards = computed(() => {
      const counts = new Map<string, number>()
      recentActivity.value.forEach((activity) => {
        const label = activity.label || 'Unknown event'
        counts.set(label, (counts.get(label) || 0) + 1)
      })
      return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 6)
    })
    const topCanonicalMessage = computed(() => {
      const [label = 'No events yet', count = 0] = historicalPreview.value?.topEvents?.[0]
        ? [toSentenceCase(historicalPreview.value.topEvents[0].label), historicalPreview.value.topEvents[0].count]
        : []

      return { label, count }
    })
    const canonicalMessageBars = computed(() => {
      const topEvents = historicalPreview.value?.topEvents || []
      const maxValue = Math.max(...topEvents.map((item) => item.count), 1)

      return topEvents
        .slice(0, 12)
        .map((item) => ({
          label: toSentenceCase(item.label),
          value: item.count,
          width: Math.max((item.count / maxValue) * 100, item.count > 0 ? 8 : 0),
        }))
    })
    const messageCoverage = computed(() => {
      const coverage = historicalPreview.value?.instrumentationCoverage
      const totalEvents = coverage?.totalEvents || 0
      const divide = (value: number) => (totalEvents > 0 ? value / totalEvents : 0)

      return {
        totalEvents,
        withJourneyId: coverage?.withJourneyId || 0,
        withSessionId: coverage?.withSessionId || 0,
        withRequestId: coverage?.withRequestId || 0,
        journeyCoverage: divide(coverage?.withJourneyId || 0),
        sessionCoverage: divide(coverage?.withSessionId || 0),
        requestCoverage: divide(coverage?.withRequestId || 0),
      }
    })
    const averageSessionMinutes = computed(() => {
      const durations = sessionSummaries.value
        .map((session) => {
          const first = new Date(session.firstSeen).getTime()
          const last = new Date(session.lastSeen).getTime()
          if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return null
          return Math.max(Math.round((last - first) / 60000), 0)
        })
        .filter((value): value is number => value !== null)

      if (!durations.length) return 0
      return Math.round(durations.reduce((total, value) => total + value, 0) / durations.length)
    })
    const brokenJourneyCount = computed(() => journeySummaries.value.filter((journey) => journey.issueLevel === 'critical').length)
    const stalledJourneyCount = computed(() => journeySummaries.value.filter((journey) => journey.issueLevel === 'warning').length)
    const errorRate = computed(() => (
      recentActivity.value.length > 0 ? eventDiagnostics.value.streamIssues / recentActivity.value.length : 0
    ))
    const requestCount = computed(() => {
      const historicalRequests = historicalPreview.value?.instrumentationCoverage?.withRequestId || 0
      if (historicalRequests > 0) {
        return historicalRequests
      }
      return Math.max(recentActivity.value.length - eventDiagnostics.value.missingRequestCount, 0)
    })
    const prizePoolSize = computed(() => readCount(
      campaignSummary.value,
      'prizePoolSize',
      'prizePool',
      'prize_pool_size',
      'availablePrizes',
      'availableRewards'
    ))
    const prizesAllocated = computed(() => readCount(
      campaignSummary.value,
      'prizesAllocated',
      'allocatedPrizes',
      'allocatedRewards',
      'reward.drop.allocated',
      'allocated'
    ))
    const mostCommonDropOff = computed(() => {
      const candidates = journeySummaries.value.filter((journey) => journey.issueLevel === 'warning' || journey.issueLevel === 'critical')
      if (!candidates.length) {
        return { label: 'No clear drop-off yet', count: 0 }
      }
      const counts = new Map<string, number>()
      candidates.forEach((journey) => {
        const label = journey.latestEvent || 'Unknown event'
        counts.set(label, (counts.get(label) || 0) + 1)
      })
      const [label = 'Unknown event', count = 0] = Array.from(counts.entries()).sort((left, right) => right[1] - left[1])[0] || []
      return { label, count }
    })
    const executiveCards = computed(() => {
      const uniqueSessions = historicalPreview.value?.totals.uniqueSessions || sessionSummaries.value.length
      const uniqueJourneys = historicalPreview.value?.totals.uniqueJourneys || journeySummaries.value.length
      const billableSessions = readNumber(campaignSummary.value.billableSessions) || readNumber(tenantSummary.value.billableSessions)
      const requests = requestCount.value
      const sessionMinutes = averageSessionMinutes.value
      const toneForRate = (value: number, healthyThreshold: number, warningThreshold: number) => (
        value >= healthyThreshold ? 'healthy' as const : value >= warningThreshold ? 'warning' as const : 'critical' as const
      )

      return [
        {
          label: 'Sessions',
          value: formatNumber(uniqueSessions),
          detail: `${formatNumber(uniqueJourneys)} journeys observed`,
          tone: uniqueSessions > 0 ? 'healthy' as const : 'warning' as const,
        },
        {
          label: 'Billable sessions',
          value: formatNumber(billableSessions),
          detail: `${formatNumber(readNumber(campaignSummary.value.subscriptionTokensBurned) || readNumber(tenantSummary.value.subscriptionTokensBurned))} subscription tokens burned`,
          tone: billableSessions > 0 ? 'healthy' as const : 'warning' as const,
        },
        {
          label: 'Requests',
          value: formatNumber(requests),
          detail: `${formatPercent(messageCoverage.value.requestCoverage)} request coverage`,
          tone: toneForRate(messageCoverage.value.requestCoverage, 0.8, 0.5),
        },
        {
          label: 'Avg session time',
          value: `${formatNumber(sessionMinutes)}m`,
          detail: sessionMinutes > 0 ? 'Mean duration across current session window' : 'Waiting for more session history',
          tone: sessionMinutes > 0 ? 'healthy' as const : 'warning' as const,
        },
        {
          label: 'Prizes allocated',
          value: formatNumber(prizesAllocated.value),
          detail: prizePoolSize.value > 0 ? `${formatNumber(prizePoolSize.value)} total in current pool` : 'Pool size not yet surfaced in tallies',
          tone: prizesAllocated.value > 0 ? 'healthy' as const : 'warning' as const,
        },
      ]
    })
    const visitsToClaimsSeries = computed(() => (
      (historicalPreview.value?.series || []).slice(-10).map((point) => ({
        date: point.date,
        users: point.uniqueActors,
        claims: point.claims,
      }))
    ))
    const visitsToClaimsMaxValue = computed(() => Math.max(...visitsToClaimsSeries.value.flatMap((point) => [point.users, point.claims]), 0))
    const trafficLights = computed(() => [
      {
        label: 'Error rate',
        value: formatPercent(errorRate.value),
        description: 'Share of the recent realtime stream carrying blocked, denied, failed, or error-like signals.',
        tone: errorRate.value > 0.08 ? 'critical' : errorRate.value > 0.03 ? 'warning' : 'healthy',
      },
      {
        label: 'Broken journeys',
        value: formatNumber(brokenJourneyCount.value),
        description: 'Journeys whose current path contains explicit breakage or failure signals.',
        tone: brokenJourneyCount.value > 0 ? 'critical' : 'healthy',
      },
      {
        label: 'Traceability',
        value: formatPercent(messageCoverage.value.journeyCoverage),
        description: 'Coverage of journey identifiers in the historical preview used for deep behavioural analysis.',
        tone: messageCoverage.value.journeyCoverage >= 0.8 ? 'healthy' : messageCoverage.value.journeyCoverage >= 0.5 ? 'warning' : 'critical',
      },
      {
        label: 'Geo coverage',
        value: historicalPreview.value
          ? formatPercent((historicalPreview.value.instrumentationCoverage.geoTaggedEvents + historicalPreview.value.instrumentationCoverage.gpsTaggedEvents) / Math.max(historicalPreview.value.instrumentationCoverage.totalEvents, 1))
          : formatPercent(0),
        description: 'Share of historical events with either coarse geo or precise GPS context.',
        tone: historicalPreview.value && historicalPreview.value.instrumentationCoverage.totalEvents > 0
          ? ((historicalPreview.value.instrumentationCoverage.geoTaggedEvents + historicalPreview.value.instrumentationCoverage.gpsTaggedEvents) / historicalPreview.value.instrumentationCoverage.totalEvents) >= 0.4
            ? 'healthy'
            : ((historicalPreview.value.instrumentationCoverage.geoTaggedEvents + historicalPreview.value.instrumentationCoverage.gpsTaggedEvents) / historicalPreview.value.instrumentationCoverage.totalEvents) >= 0.15
              ? 'warning'
              : 'critical'
          : 'warning',
      },
    ])
    const dropOffCards = computed(() => [
      {
        label: 'Likely drop-off point',
        value: mostCommonDropOff.value.label,
        description: `${formatNumber(mostCommonDropOff.value.count)} stalled or broken journeys most recently end here.`,
      },
      {
        label: 'Stalled journeys',
        value: formatNumber(stalledJourneyCount.value),
        description: 'Journeys that appear to stop before redeem or later-stage completion.',
      },
      {
        label: 'Billable sessions',
        value: formatNumber(readNumber(campaignSummary.value.billableSessions) || readNumber(tenantSummary.value.billableSessions)),
        description: 'Current tally surfaced through the analytics summary contract.',
      },
      {
        label: 'Subscription tokens burned',
        value: formatNumber(readNumber(campaignSummary.value.subscriptionTokensBurned) || readNumber(tenantSummary.value.subscriptionTokensBurned)),
        description: 'Current token-billing burn tally for this campaign scope.',
      },
    ])
    const simulatorFallbackGeoPoints = computed(() => {
      const campaignId = focusCampaign.value?.id || requestedCampaignId.value
      const readPersistedPoints = (storageKey: string) => {
        const raw = window.localStorage.getItem(storageKey)
        if (!raw) return []
        const parsed = JSON.parse(raw) as {
          campaignId?: string
          capturedAt?: string
          points?: Array<{ lat: number; lon: number; source: 'geo' | 'gps'; actorId: string; label: string }>
        }

        if (!Array.isArray(parsed.points) || !parsed.capturedAt) return []

        const ageMs = Date.now() - new Date(parsed.capturedAt).getTime()
        if (!Number.isFinite(ageMs) || ageMs > 1000 * 60 * 15) return []

        return parsed.points.map((point, index) => ({
          id: `sim-${parsed.campaignId || 'latest'}-${point.actorId}-${index}`,
          x: ((point.lon + 180) / 360) * 100,
          y: ((90 - point.lat) / 180) * 50,
          radius: point.source === 'gps' ? 1.05 : 0.85,
          source: point.source,
          label: point.label,
          occurredAt: parsed.capturedAt!,
        }))
      }

      if (!campaignId) return readPersistedPoints(`${SIMULATOR_GEO_STORAGE_KEY}:latest`) as Array<{
        id: string
        x: number
        y: number
        radius: number
        source: 'geo' | 'gps' | 'campaign_location'
        label: string
        occurredAt: string
      }>

      try {
        const campaignPoints = readPersistedPoints(`${SIMULATOR_GEO_STORAGE_KEY}:${campaignId}`)
        if (campaignPoints.length) {
          return campaignPoints as Array<{
            id: string
            x: number
            y: number
            radius: number
            source: 'geo' | 'gps' | 'campaign_location'
            label: string
            occurredAt: string
          }>
        }
        return readPersistedPoints(`${SIMULATOR_GEO_STORAGE_KEY}:latest`) as Array<{
          id: string
          x: number
          y: number
          radius: number
          source: 'geo' | 'gps' | 'campaign_location'
          label: string
          occurredAt: string
        }>
      } catch {
        return []
      }
    })
    const geoPoints = computed(() => {
      const livePoints = recentActivity.value
        .filter((activity) => Boolean(activity.geoPoint))
        .map((activity) => ({
          id: activity.id,
          x: ((activity.geoPoint!.lon + 180) / 360) * 100,
          y: ((90 - activity.geoPoint!.lat) / 180) * 50,
          radius: activity.geoPoint!.source === 'gps' ? 1.05 : 0.85,
          source: activity.geoPoint!.source,
          label: activity.label,
          occurredAt: activity.occurredAt,
        }))

      return livePoints.length ? livePoints : simulatorFallbackGeoPoints.value
    })
    const geoOnlyPoints = computed(() => geoPoints.value.filter((point) => point.source === 'geo'))
    const preciseGeoPoints = computed(() => geoPoints.value.filter((point) => point.source === 'gps' || point.source === 'campaign_location'))
    const geoSummary = computed(() => {
      const points = geoPoints.value
      const gpsCount = points.filter((point) => point.source === 'gps').length
      const campaignLocationCount = points.filter((point) => point.source === 'campaign_location').length
      return {
        total: points.length,
        gpsCount,
        campaignLocationCount,
        geoCount: points.length - gpsCount - campaignLocationCount,
      }
    })
    const geoSourceCards = computed(() => ([
      { label: 'Geo', count: geoSummary.value.geoCount },
      { label: 'GPS', count: geoSummary.value.gpsCount },
      { label: 'Campaign location', count: geoSummary.value.campaignLocationCount },
    ].filter((item) => item.count > 0)))
    const historicalGeoMaxValue = computed(() => Math.max(...(historicalGeoFallback.value?.geoSeries || []).map((point) => point.geoPoints), 0))
    const historicalGeoSourceCards = computed(() => (
      (historicalGeoFallback.value?.geoSources || []).map((source) => ({
        label: toSentenceCase(source.label),
        count: source.count,
      }))
    ))
    const geoActivity = computed(() => recentActivity.value
      .filter((activity) => Boolean(activity.geoPoint))
      .slice(0, 8))
    const geoDensityCells = computed(() => {
      const cells = new Map<string, { id: string; count: number; label: string }>()
      geoPoints.value.forEach((point) => {
        const xBucket = Math.min(3, Math.max(0, Math.floor(point.x / 25)))
        const yBucket = Math.min(1, Math.max(0, Math.floor(point.y / 25)))
        const id = `${xBucket}-${yBucket}`
        const next = cells.get(id) || {
          id,
          count: 0,
          label: `${yBucket === 0 ? 'North' : 'South'} ${['West', 'Central', 'East', 'Far East'][xBucket]}`,
        }
        next.count += 1
        cells.set(id, next)
      })
      return Array.from(cells.values()).sort((left, right) => right.count - left.count)
    })
    const leaderboardSummaryCards = computed(() => {
      const totalRows = leaderboardRows.value.length
      const topRow = leaderboardRows.value[0]
      const activeBoards = leaderboardCards.value.filter((board) => board.entries.length > 0).length
      return [
        {
          label: 'Active boards',
          value: formatNumber(activeBoards),
          detail: `${formatNumber(leaderboardCards.value.length)} configured leaderboard groups`,
        },
        {
          label: 'Ranked rows',
          value: formatNumber(totalRows),
          detail: 'Total visible leaderboard entries returned in the current snapshot.',
        },
        {
          label: 'Top score',
          value: topRow ? formatNumber(topRow.score) : '0',
          detail: topRow ? `${topRow.board} · ${shortActorId(topRow.actorId)}` : 'Waiting for ranked activity',
        },
        {
          label: 'Backing store',
          value: 'Valkey',
          detail: 'Proxied through cache-manager for operational comparison.',
        },
      ]
    })
    const exportCards = computed<Array<{
      id: AnalyticsExportDataset | 'users' | 'surveys' | 'quizzes' | 'games'
      label: string
      detail: string
      enabled: boolean
      actionLabel: string
    }>>(() => {
      const focusName = focusCampaign.value?.name || 'Current scope'
      return [
        {
          id: 'events',
          label: 'Event stream export',
          detail: `${recentActivity.value.length} recent native events for ${focusName}.`,
          enabled: hasExportsAccess.value && recentActivity.value.length > 0,
          actionLabel: 'Download CSV',
        },
        {
          id: 'historical',
          label: 'Historical DuckDB export',
          detail: hasHistoricalExportAccess.value
            ? 'Parquet-backed historical deep query export for the current campaign scope.'
            : 'Historical parquet export remains locked without DuckDB or advanced analytics access.',
          enabled: Boolean(hasExportsAccess.value && hasHistoricalExportAccess.value && focusCampaign.value?.id),
          actionLabel: 'Download CSV',
        },
        {
          id: 'journeys',
          label: 'Journey summary export',
          detail: `${journeySummaries.value.length} tracked journeys in the current stream window.`,
          enabled: hasExportsAccess.value && journeySummaries.value.length > 0,
          actionLabel: 'Download CSV',
        },
        {
          id: 'sessions',
          label: 'Session summary export',
          detail: `${sessionSummaries.value.length} tracked sessions with native activity in the current window.`,
          enabled: hasExportsAccess.value && sessionSummaries.value.length > 0,
          actionLabel: 'Download CSV',
        },
        {
          id: 'geo',
          label: 'Geo points export',
          detail: `${geoSummary.value.total} geo/gps points currently available in native analytics.`,
          enabled: hasExportsAccess.value && geoSummary.value.total > 0,
          actionLabel: 'Download CSV',
        },
        {
          id: 'diagnostics',
          label: 'Diagnostics snapshot export',
          detail: `${diagnostics.value.length + diagnosticHighlights.value.length} current inferred issues and priority signals.`,
          enabled: hasExportsAccess.value && (diagnostics.value.length > 0 || diagnosticHighlights.value.length > 0),
          actionLabel: 'Download CSV',
        },
        {
          id: 'portfolio',
          label: 'Portfolio comparison export',
          detail: `${rankedCampaigns.value.length} campaigns ranked by current performance tallies.`,
          enabled: hasExportsAccess.value && rankedCampaigns.value.length > 0,
          actionLabel: 'Download CSV',
        },
        {
          id: 'leaderboards',
          label: 'Leaderboard export',
          detail: `${leaderboardCards.value.reduce((count, board) => count + board.entries.length, 0)} ranked actor rows across the current leaderboard set.`,
          enabled: hasExportsAccess.value && leaderboardCards.value.some((board) => board.entries.length > 0),
          actionLabel: 'Download CSV',
        },
        {
          id: 'users',
          label: 'User-level export',
          detail: hasUserPiiAccess.value
            ? 'Canonical participant export with consent, profile, and sensitive user fields from the v10 user-campaign model.'
            : 'Canonical participant roster export with sensitive fields redacted. Add the PII entitlement to include email, names, mobile, and date-of-birth fields.',
          enabled: hasExportsAccess.value && Boolean(focusCampaign.value?.id),
          actionLabel: hasUserPiiAccess.value ? 'Download CSV' : 'Download redacted CSV',
        },
        {
          id: 'surveys',
          label: 'Survey responses export',
          detail: orgDetails.value?.features.includes('analytics.surveys') || orgDetails.value?.features.includes('experience.surveys')
            ? 'Survey exports are planned but the canonical reporting dataset is not available in this app yet.'
            : 'Survey exports remain locked without the survey/reporting entitlement.',
          enabled: false,
          actionLabel: 'Not ready',
        },
        {
          id: 'quizzes',
          label: 'Quiz results export',
          detail: Boolean(
            orgDetails.value?.features.includes('analytics.quizzes') ||
            orgDetails.value?.features.includes('experience.quizzes') ||
            orgDetails.value?.features.includes('analytics.advanced')
          )
            ? 'Canonical quiz-attempt export from the v10 quiz play-state model.'
            : 'Quiz exports remain locked without the quiz/reporting entitlement.',
          enabled: Boolean(hasExportsAccess.value && focusCampaign.value?.id && (
            orgDetails.value?.features.includes('analytics.quizzes') ||
            orgDetails.value?.features.includes('experience.quizzes') ||
            orgDetails.value?.features.includes('analytics.advanced')
          )),
          actionLabel: 'Download CSV',
        },
        {
          id: 'games',
          label: 'Game outcomes export',
          detail: orgDetails.value?.features.includes('analytics.games') || orgDetails.value?.features.includes('experience.games')
            ? 'Game exports are planned but the canonical reporting dataset is not available in this app yet.'
            : 'Game exports remain locked without the game/reporting entitlement.',
          enabled: false,
          actionLabel: 'Not ready',
        },
      ]
    })
    const eventDiagnostics = computed(() => ({
      streamIssues: recentActivity.value.filter((activity) => /error|fail|denied|blocked/i.test(activity.label)).length,
      blockedCount: recentActivity.value.filter((activity) => /blocked|denied/i.test(activity.label)).length,
      failureCount: recentActivity.value.filter((activity) => /error|fail/i.test(activity.label)).length,
      authSignals: recentActivity.value.filter((activity) => /login|logged in|auth|token|session-start/i.test(activity.label)).length,
      missingJourneyCount: recentActivity.value.filter((activity) => !activity.journeyId).length,
      missingSessionCount: recentActivity.value.filter((activity) => !activity.sessionId).length,
      missingRequestCount: recentActivity.value.filter((activity) => !activity.requestId).length,
      missingClientIpCount: recentActivity.value.filter((activity) => !activity.clientIp).length,
      missingUserAgentCount: recentActivity.value.filter((activity) => !activity.userAgent).length,
      termsAuditCount: recentActivity.value.filter((activity) => Boolean(activity.acceptedAt || activity.acceptedTermsVersion || activity.termsAndConditionsCheck === true)).length,
    }))
    const latestActivityAgeMinutes = computed(() => {
      const occurredAt = recentActivity.value[0]?.occurredAt
      if (!occurredAt) return null
      const parsed = new Date(occurredAt).getTime()
      if (Number.isNaN(parsed)) return null
      return Math.max(Math.round((Date.now() - parsed) / 60000), 0)
    })
    const diagnosticGroups = computed(() => ({
      observability: [
        {
          label: 'Journey gaps',
          value: eventDiagnostics.value.missingJourneyCount,
          description: 'Recent events missing a journey identifier.',
        },
        {
          label: 'Session gaps',
          value: eventDiagnostics.value.missingSessionCount,
          description: 'Recent events missing a session identifier.',
        },
        {
          label: 'Request gaps',
          value: eventDiagnostics.value.missingRequestCount,
          description: 'Recent events missing a request identifier.',
        },
        {
          label: 'IP gaps',
          value: eventDiagnostics.value.missingClientIpCount,
          description: 'Recent events missing client IP context.',
        },
        {
          label: 'User-agent gaps',
          value: eventDiagnostics.value.missingUserAgentCount,
          description: 'Recent events missing user-agent context.',
        },
      ],
      operations: [
        {
          label: 'Error-like events',
          value: eventDiagnostics.value.streamIssues,
          description: 'Blocked, denied, failed, or error-like events in the current stream.',
        },
      ],
      security: [
        {
          label: 'Blocked or denied',
          value: eventDiagnostics.value.blockedCount,
          description: 'Recent access-control or policy denials in the stream.',
        },
        {
          label: 'Failure-like auth',
          value: eventDiagnostics.value.failureCount,
          description: 'Recent auth or runtime failure signals inferred from event names.',
        },
        {
          label: 'Auth/session signals',
          value: eventDiagnostics.value.authSignals,
          description: 'Recent login, session-start, or token-related activity in the stream.',
        },
      ],
      activity: [
        {
          label: 'Recent window',
          value: recentActivity.value.length,
          description: 'Number of recent events currently loaded into the native stream window.',
        },
        {
          label: 'Latest event age',
          value: latestActivityAgeMinutes.value === null ? '—' : `${latestActivityAgeMinutes.value}m`,
          description: 'How stale the newest native analytics event appears to be.',
        },
        {
          label: 'Terms audit events',
          value: eventDiagnostics.value.termsAuditCount,
          description: 'Recent events carrying explicit terms-acceptance audit fields.',
        },
      ],
    }))
    const campaignHealthCards = computed<HealthCard[]>(() => {
      const focus = focusCampaign.value
      if (!focus) {
        return []
      }

      const blocked = eventDiagnostics.value.blockedCount
      const failures = eventDiagnostics.value.failureCount
      const traceabilityGaps = eventDiagnostics.value.missingJourneyCount
        + eventDiagnostics.value.missingSessionCount
        + eventDiagnostics.value.missingRequestCount

      const healthTone: HealthCard['tone'] =
        failures > 0 || blocked > 0
          ? 'critical'
          : traceabilityGaps > 0
            ? 'warning'
            : 'healthy'

      return [
        {
          label: 'Overall health',
          value: healthTone === 'critical' ? 'Critical' : healthTone === 'warning' ? 'Needs attention' : 'Healthy',
          detail: healthTone === 'critical'
            ? 'Recent failure or denial signals require attention.'
            : healthTone === 'warning'
              ? 'The campaign is running but has operational or traceability gaps.'
              : 'No major failure signals are visible in the current window.',
          tone: healthTone,
        },
        {
          label: 'Breakage risk',
          value: formatNumber(failures + blocked),
          detail: 'Recent blocked, denied, error, or failure-like events.',
          tone: failures + blocked > 0 ? 'critical' : 'healthy',
        },
        {
          label: 'Traceability',
          value: formatNumber(traceabilityGaps),
          detail: 'Missing journey, session, or request identifiers in the current stream.',
          tone: traceabilityGaps > 0 ? 'warning' : 'healthy',
        },
      ]
    })
    const diagnosticHighlights = computed(() => {
      const highlights: Array<{ label: string; value: string; description: string }> = []
      const events = recentActivity.value
      const blocked = events.filter((activity) => /blocked|denied/i.test(activity.label)).length
      const failures = events.filter((activity) => /fail|error/i.test(activity.label)).length
      const missingContext = eventDiagnostics.value.missingJourneyCount
        + eventDiagnostics.value.missingSessionCount
        + eventDiagnostics.value.missingRequestCount
        + eventDiagnostics.value.missingClientIpCount
        + eventDiagnostics.value.missingUserAgentCount
      const stalledJourneys = journeySummaries.value.filter((journey) => journey.issueLevel === 'warning').length
      const brokenJourneys = journeySummaries.value.filter((journey) => journey.issueLevel === 'critical').length

      if (blocked > 0) {
        highlights.push({
          label: 'Blocked or denied',
          value: formatNumber(blocked),
          description: 'Recent access or flow-control denials detected in the stream.',
        })
      }

      if (failures > 0) {
        highlights.push({
          label: 'Failure-like events',
          value: formatNumber(failures),
          description: 'Recent failures or errors inferred from event names.',
        })
      }

      if (missingContext > 0) {
        highlights.push({
          label: 'Traceability gaps',
          value: formatNumber(missingContext),
          description: 'Missing journey, session, or request identifiers reduce trace quality.',
        })
      }

      if (brokenJourneys > 0) {
        highlights.push({
          label: 'Broken journeys',
          value: formatNumber(brokenJourneys),
          description: 'Journeys with error-like events or explicit breakage indicators.',
        })
      }

      if (stalledJourneys > 0) {
        highlights.push({
          label: 'Stalled journeys',
          value: formatNumber(stalledJourneys),
          description: 'Journeys that appear to stop before redeem or later-stage completion.',
        })
      }

      return highlights
    })
    const diagnosticEventFamilies = computed(() => {
      const counts = new Map<string, number>()
      recentActivity.value
        .filter((activity) => /error|fail|denied|blocked/i.test(activity.label))
        .forEach((activity) => {
          counts.set(activity.label, (counts.get(activity.label) || 0) + 1)
        })

      return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((left, right) => right.count - left.count)
        .slice(0, 6)
    })

    const diagnostics = computed<DiagnosticItem[]>(() => {
      const items: DiagnosticItem[] = []
      const focus = focusCampaign.value

      if (!focus) {
        return items
      }

      if (focus.lifecycleStatus === 'Paused') {
        items.push({
          title: 'Campaign paused',
          description: `${focus.name} is currently paused, so activity should remain flat until it is resumed.`,
          level: 'info',
        })
      }

      if (focus.metrics.registered > 0 && focus.metrics.claimed === 0) {
        items.push({
          title: 'Registrations without claims',
          description: 'Registrations are arriving, but no claims have been recorded yet for this campaign.',
          level: 'warning',
        })
      }

      const streamIssues = recentActivity.value.filter((activity) => /error|fail|denied|blocked/i.test(activity.label))
      if (streamIssues.length) {
        items.push({
          title: 'Error signals in recent stream',
          description: `${streamIssues.length} recent events look like errors, denials, or blocked transitions.`,
          level: 'critical',
        })
      }

      const missingJourneyCount = recentActivity.value.filter((activity) => !activity.journeyId).length
      if (missingJourneyCount > 0) {
        items.push({
          title: 'Journey context gaps',
          description: `${missingJourneyCount} recent events were missing a journey identifier, which limits traceability.`,
          level: 'warning',
        })
      }

      const stalledJourneys = journeySummaries.value.filter((journey) => journey.issueLevel === 'warning').length
      if (stalledJourneys > 0) {
        items.push({
          title: 'Stalled journeys detected',
          description: `${stalledJourneys} journeys appear to stop before redeem or another later-stage completion signal.`,
          level: 'warning',
        })
      }

      const brokenJourneys = journeySummaries.value.filter((journey) => journey.issueLevel === 'critical').length
      if (brokenJourneys > 0) {
        items.push({
          title: 'Broken journeys detected',
          description: `${brokenJourneys} journeys contain explicit error or denial signals in the current analytics window.`,
          level: 'critical',
        })
      }

      return items
    })

    const activityPanelLines = computed(() => {
      if (!focusCampaign.value) {
        return ['Recent stream activity', 'Current analytics scope']
      }

      const rawName = focusCampaign.value.name || ''
      const [campaignId, campaignDateTime] = rawName.split('_', 2)

      if (campaignDateTime) {
        return [
          'Recent stream activity for',
          campaignId,
          campaignDateTime,
        ]
      }

      return [
        'Recent stream activity for',
        rawName,
      ]
    })

    const lastUpdatedLabel = computed(() => {
      if (!lastUpdatedAt.value) return 'Not refreshed yet'
      return `Updated ${formatRelativeTime(lastUpdatedAt.value)}`
    })

    const ensureAccess = async (): Promise<boolean> => {
      await authStore.checkAuth()
      if (!authStore.isAuthenticated) {
        await router.replace({ path: '/' })
        return false
      }
      return true
    }

    const switchMode = async (mode: AnalyticsMode) => {
      await router.replace({
        name: 'DashboardAnalytics',
        query: {
          ...route.query,
          mode,
        },
      })
    }

    const setSection = async (section: AdvancedSection) => {
      await router.replace({
        name: 'DashboardAnalytics',
        query: {
          ...route.query,
          section,
        },
      })
    }

    const focusOnCampaign = async (campaignId: string) => {
      await router.replace({
        name: 'DashboardAnalytics',
        query: {
          ...route.query,
          campaignId,
        },
      })
    }

    const goDashboard = async () => {
      await router.push({ name: 'Dashboard' })
    }

    const goBilling = async () => {
      await router.push({ name: 'billing' })
    }

    const openOpenSearchAddon = async () => {
      await router.push({
        name: 'DashboardAnalyticsOpenSearch',
        query: {
          campaignId: focusCampaign.value?.id || requestedCampaignId.value || undefined,
        },
      })
    }

    const openHistoricalAnalytics = async () => {
      await router.push({
        name: 'DashboardAnalyticsHistorical',
        query: {
          campaignId: focusCampaign.value?.id || requestedCampaignId.value || undefined,
        },
      })
    }

    const openVisualBoard = async () => {
      await router.push({
        name: 'DashboardAnalyticsVisual',
        query: {
          campaignId: focusCampaign.value?.id || requestedCampaignId.value || undefined,
        },
      })
    }

    const openSimulator = async () => {
      await router.push({
        name: 'DashboardAnalyticsSimulator',
        query: {
          campaignId: focusCampaign.value?.id || requestedCampaignId.value || undefined,
        },
      })
    }

    const downloadBlob = (filename: string, blob: Blob) => {
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
    }

    const loadExportJobs = async () => {
      if (!authStore.isAuthenticated || !hasExportsAccess.value) {
        exportJobs.value = []
        return
      }

      try {
        exportJobs.value = await analyticsService.listExports(12)
      } catch (error) {
        console.warn('Failed to load analytics export jobs:', error)
      }
    }

    const scheduleExportPolling = () => {
      if (exportJobsTimer) {
        window.clearTimeout(exportJobsTimer)
        exportJobsTimer = null
      }

      const hasActiveJobs = exportJobs.value.some((job) => job.status === 'QUEUED' || job.status === 'RUNNING')
      if (!hasActiveJobs) return

      exportJobsTimer = window.setTimeout(async () => {
        await loadExportJobs()
      }, 4000)
    }

    const exportDataset = async (dataset: AnalyticsExportDataset) => {
      if (!focusCampaign.value && dataset !== 'portfolio') {
        return
      }

      const job = await analyticsService.requestExport(dataset, focusCampaign.value?.id)
      exportJobs.value = [job, ...exportJobs.value.filter((existing) => existing.id !== job.id)]
      if (job.status === 'COMPLETE') {
        await downloadExportJob(job.id)
      } else {
        await loadExportJobs()
      }
    }

    const downloadExportJob = async (jobId: string) => {
      const { blob, fileName } = await analyticsService.downloadExport(jobId)
      downloadBlob(fileName, blob)
    }

    const refreshAnalytics = async () => {
      const canAccess = await ensureAccess()
      if (!canAccess) return

      if (
        !campaigns.value.length &&
        !recentActivity.value.length &&
        !historicalPreview.value &&
        !Object.keys(campaignSummary.value).length
      ) {
        isLoading.value = true
      }

      try {
        const [managedCampaigns, org] = await Promise.all([
          campaignsService.listCampaigns(),
          billingService.getOrgDetails(),
        ])

        orgDetails.value = org
        await loadExportJobs()

        const batchMetrics = managedCampaigns.length
          ? await analyticsService.getCampaignAnalyticsBatch(managedCampaigns.map((campaign) => campaign.id))
          : []
        const metricsByCampaignId = new Map(batchMetrics.map((item) => [item.campaignId, item]))

        campaigns.value = managedCampaigns.map((campaign) => {
          const metrics = metricsByCampaignId.get(campaign.id)
          return {
            ...campaign,
            lifecycleStatus: deriveLifecycleStatus(campaign),
            metrics: {
              registered: metrics?.registered ?? 0,
              claimed: metrics?.claimed ?? 0,
              redeemed: metrics?.redeemed ?? 0,
              shared: metrics?.shared ?? 0,
            },
          }
        })

        const focus = (() => {
          if (!campaigns.value.length) return null
          if (requestedCampaignId.value) {
            return campaigns.value.find((campaign) => campaign.id === requestedCampaignId.value) || campaigns.value[0]
          }
          return [...campaigns.value].sort((left, right) => right.metrics.registered - left.metrics.registered)[0] || campaigns.value[0]
        })()

        const requests: Promise<unknown>[] = [
          analyticsService.getTallies({ scope: 'org', id: org.id }),
        ]

        if (focus?.tenantId) {
          requests.push(analyticsService.getTallies({ scope: 'tenant', id: focus.tenantId }))
        } else {
          requests.push(Promise.resolve({ tenant: {} }))
        }

        if (focus?.id) {
          requests.push(analyticsService.getTallies({
            scope: 'campaign',
            id: focus.id,
            includeStream: true,
            streamLimit: 80,
            includeRelated: true,
            relatedLimit: 24,
          }))
          requests.push(analyticsService.queryHistorical({
            campaignId: focus.id,
            days: 30,
            maxObjects: 80,
          }).catch(() => null))
        } else {
          requests.push(Promise.resolve({ campaign: {} }))
          requests.push(Promise.resolve(null))
        }

        const [orgTallies, tenantTallies, campaignTallies, historicalGeoQuery] = await Promise.all(requests)

        orgSummary.value = asSummaryRecord((orgTallies as Record<string, Record<string, AnalyticsScopeSummary>>)?.org?.[org.id])
        tenantSummary.value = asSummaryRecord(
          focus?.tenantId
            ? (tenantTallies as Record<string, Record<string, AnalyticsScopeSummary>>)?.tenant?.[focus.tenantId]
            : {}
        )
        campaignSummary.value = asSummaryRecord(
          focus?.id
            ? (campaignTallies as Record<string, Record<string, AnalyticsScopeSummary>>)?.campaign?.[focus.id]
            : {}
        )
        campaignJourneySummaries.value = Array.isArray(
          (campaignSummary.value as Record<string, unknown>).journeys
        )
          ? (campaignSummary.value as Record<string, unknown>).journeys as AnalyticsRelatedSummary[]
          : []
        campaignSessionSummaries.value = Array.isArray(
          (campaignSummary.value as Record<string, unknown>).sessions
        )
          ? (campaignSummary.value as Record<string, unknown>).sessions as AnalyticsRelatedSummary[]
          : []

        const rawStream = Array.isArray(campaignSummary.value.stream)
          ? campaignSummary.value.stream as AnalyticsStreamRecord[]
          : []

        recentActivity.value = rawStream
          .map(parseActivityRecord)
          .sort(sortByOccurredAtDesc)
        historicalPreview.value = historicalGeoQuery as HistoricalAnalyticsQuery | null
        historicalGeoFallback.value = (
          !recentActivity.value.some((activity) => Boolean(activity.geoPoint)) &&
          historicalGeoQuery &&
          (historicalGeoQuery as HistoricalAnalyticsQuery).totals.geoPoints > 0
        )
          ? historicalGeoQuery as HistoricalAnalyticsQuery
          : null

        if (focus?.id) {
          const leaderboardRequests = await Promise.allSettled([
            analyticsService.getLeaderboards({ eventName: 'user.registration.succeeded', campaignId: focus.id, limit: 20 }),
            analyticsService.getLeaderboards({ eventName: 'reward.drop.claimed', campaignId: focus.id, limit: 20 }),
            analyticsService.getLeaderboards({ eventName: 'reward.drop.redeemed', campaignId: focus.id, limit: 20 }),
            analyticsService.getLeaderboards({ eventName: 'share.link.shared', campaignId: focus.id, limit: 20 }),
            // Quiz board powered by cache-manager's dual-key Valkey shape —
            // returns handle (real player_name with anonymized fallback) +
            // bestScore + lastScore per player in this campaign. Used by the
            // Quiz leaderboard chart (see quizLeaderboardChart computed).
            analyticsService.getLeaderboards({ eventName: 'quiz.completed.score', campaignId: focus.id, limit: 10 }),
          ])

          leaderboards.value = {
            Registrations: leaderboardRequests[0].status === 'fulfilled' ? leaderboardRequests[0].value : [],
            Claims: leaderboardRequests[1].status === 'fulfilled' ? leaderboardRequests[1].value : [],
            Redeems: leaderboardRequests[2].status === 'fulfilled' ? leaderboardRequests[2].value : [],
            Shares: leaderboardRequests[3].status === 'fulfilled' ? leaderboardRequests[3].value : [],
            Quiz: leaderboardRequests[4].status === 'fulfilled' ? leaderboardRequests[4].value : [],
          }
        } else {
          leaderboards.value = {}
        }

        lastUpdatedAt.value = new Date().toISOString()

        // Force the over-time chart loaders to fire after campaigns + focus
        // resolve. The watcher on [analyticsRange, focusCampaign.value?.id]
        // (registered above with immediate: true) fires once on mount with
        // focusCampaign === null and bails out, and has been observed not to
        // refire when focusCampaign flips to a real campaign — leaving the
        // Registrations/Gameplays/Prizes Over Time charts empty on first
        // paint until the user re-picks the range. Explicit calls here are
        // idempotent if the watcher does refire.
        void loadChartSeries()
        void loadOverTime()
      } catch (error: unknown) {
        console.error('Failed to load analytics workspace:', error)
        loadError.value = error instanceof Error
          ? error.message
          : 'Unable to load analytics right now.'
      } finally {
        isLoading.value = false
      }
    }

    const scheduleAnalyticsRefresh = () => {
      if (analyticsRefreshTimer) {
        window.clearTimeout(analyticsRefreshTimer)
      }

      analyticsRefreshTimer = window.setTimeout(async () => {
        await refreshAnalytics()
        scheduleAnalyticsRefresh()
      }, AUTO_REFRESH_MS)
    }

    onMounted(async () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      await refreshAnalytics()
      // Auto-refresh polling deliberately not scheduled — this screen is
      // load-once-and-present, not a live dashboard. Manual refresh stays
      // available via the Refresh button which calls refreshAnalytics
      // directly. The recursive setTimeout pattern in
      // scheduleAnalyticsRefresh is kept for any future opt-in re-enable
      // but no longer fires on mount.
      await claimStripeReturn()
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    })

    onUnmounted(() => {
      if (analyticsRefreshTimer) {
        window.clearTimeout(analyticsRefreshTimer)
      }
      if (exportJobsTimer) {
        window.clearTimeout(exportJobsTimer)
      }
      if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
        document.documentElement.style.overflow = ''
      }
    })

    watch(() => [route.query.campaignId, route.query.mode], async () => {
      if (authStore.isAuthenticated) {
        await refreshAnalytics()
      }
    })

    watch(exportJobs, () => {
      scheduleExportPolling()
    }, { deep: true })

    watch(journeySummaries, (summaries) => {
      if (!summaries.length) {
        selectedJourneyId.value = ''
        return
      }
      if (!summaries.some((journey) => journey.id === selectedJourneyId.value)) {
        selectedJourneyId.value = summaries[0].id
      }
    }, { immediate: true })

    watch(selectedJourneyId, async (journeyId) => {
      if (!journeyId || !authStore.isAuthenticated) {
        selectedJourneyTallies.value = {}
        selectedJourneyActivity.value = []
        return
      }

      try {
        const tallies = await analyticsService.getTallies({
          scope: 'journey',
          id: journeyId,
          includeStream: true,
          streamLimit: 60,
        })
        selectedJourneyTallies.value = asSummaryRecord(tallies?.journey?.[journeyId])
        selectedJourneyActivity.value = Array.isArray(selectedJourneyTallies.value.stream)
          ? (selectedJourneyTallies.value.stream as AnalyticsStreamRecord[]).map(parseActivityRecord).sort(sortByOccurredAtDesc)
          : []
      } catch (error) {
        console.warn('Failed to load journey tallies:', error)
        selectedJourneyTallies.value = {}
        selectedJourneyActivity.value = []
      }
    }, { immediate: true })

    watch(sessionSummaries, (summaries) => {
      if (!summaries.length) {
        selectedSessionId.value = ''
        return
      }
      if (!summaries.some((session) => session.id === selectedSessionId.value)) {
        selectedSessionId.value = summaries[0].id
      }
    }, { immediate: true })

    watch(selectedSessionId, async (sessionId) => {
      if (!sessionId || !authStore.isAuthenticated) {
        selectedSessionTallies.value = {}
        selectedSessionActivity.value = []
        return
      }

      try {
        const tallies = await analyticsService.getTallies({
          scope: 'session',
          id: sessionId,
          includeStream: true,
          streamLimit: 60,
        })
        selectedSessionTallies.value = asSummaryRecord(tallies?.session?.[sessionId])
        selectedSessionActivity.value = Array.isArray(selectedSessionTallies.value.stream)
          ? (selectedSessionTallies.value.stream as AnalyticsStreamRecord[]).map(parseActivityRecord).sort(sortByOccurredAtDesc)
          : []
      } catch (error) {
        console.warn('Failed to load session tallies:', error)
        selectedSessionTallies.value = {}
        selectedSessionActivity.value = []
      }
    }, { immediate: true })

    return {
      activeSection,
      activityPanelLines,
      advancedSections: ADVANCED_SECTIONS,
      visibleAdvancedSections,
      showGeoSurfaces,
      campaignBoundary,
      advancedChartsSummary,
      analyticsHeading,
      analyticsMode,
      campaignScheduleLabel,
      campaignHealthCards,
      campaigns,
      diagnosticGroups,
      diagnosticHighlights,
      diagnosticEventFamilies,
      diagnostics,
      downloadExportJob,
      dropOffCards,
      eventFamilyCards,
      eventMixBars,
      executiveCards,
      focusCampaign,
      focusBoardMetrics,
      focusOnCampaign,
      exportCards,
      exportDataset,
      exportJobs,
      formatCampaignSelectLabel,
      formatActivityActorType,
      formatActivityIds,
      lookupCampaignName,
      formatDateTime,
      formatNumber,
      formatPercent,
      formatRelativeTime,
      getRelativeWidth,
      geoActivity,
      geoDensityCells,
      geoPoints,
      historicalGeoFallback,
      historicalGeoMaxValue,
      historicalGeoSourceCards,
      geoSourceCards,
      geoSummary,
      geoOnlyPoints,
      goBilling,
      goDashboard,
      hasAdvancedAccess,
      hasDiagnosticsAccess,
      hasExportsAccess,
      hasGeoAccess,
      hasJourneyExplorerAccess,
      hasOpenSearchAccess,
      isExportDataset,
      hasUserPiiAccess,
      isLoading,
      journeySummaries,
      lastUpdatedLabel,
      leaderboardCards,
      loadError,
      canonicalMessageBars,
      historicalPreview,
      chartWindow,
      chartWindowOptions,
      chartSeries,
      chartLoading,
      chartError,
      overTimeRange,
      overTimeRangeOptions,
      overTimeRangeLabel,
      overTimeSeries,
      overTimeLoading,
      overTimeError,
      overTimeRegistrationsChart,
      overTimeGameplaysChart,
      overTimePrizesAllocatedChart,
      overTimeTotals,
      funnelChart,
      quizDropOffChart,
      quizLeaderboardChart,
      quizLeaderboardChartOptions,
      surveyTopAnswerChart,
      arFunnelChart,
      arDurationChart,
      conversionChart,
      conversionRateChart,
      percentageChartOptions,
      geoPointsChart,
      analyticsRange,
      analyticsRangeLabel,
      dataSpan,
      chartCommonOptions,
      stackedBarOptions,
      horizontalBarOptions,
      downloadEntitlement,
      downloadEntitlementLoading,
      downloadBusy,
      downloadError,
      paywallModal,
      showActivityModal,
      paywallExtraPacks,
      uniqueActorsInChart,
      packsNeededForAll,
      entitlementCoversAll,
      onDownloadClick,
      confirmPurchasePack,
      startCsvDownload,
      includeAnswersInCsv,
      campaignKind,
      campaignHasGeoPoints,
      geoSectionOpen,
      quizSectionOpen,
      surveySectionOpen,
      arSectionOpen,
      answerSummary,
      answerSummaryLoading,
      answerSummaryError,
      responsesModalOpen,
      leaderboardRows,
      leaderboardSummaryCards,
      messageCoverage,
      openOpenSearchAddon,
      openHistoricalAnalytics,
      openSimulator,
      openVisualBoard,
      rankedCampaigns,
      ratioCards,
      recentActivity,
      refreshAnalytics,
      requiresAdvancedAccess,
      preciseGeoPoints,
      selectedJourneyEvents,
      selectedJourneyId,
      selectedJourneyTransitions,
      selectedJourneyInsights,
      selectedJourneyMetrics,
      selectedJourneySummary,
      selectedSessionEvents,
      selectedSessionId,
      selectedSessionInsights,
      selectedSessionMetrics,
      selectedSessionSummary,
      sessionSummaries,
      setSection,
      shortActorId,
      summaryCards,
      switchMode,
      trafficLights,
      eventDiagnostics,
      topCanonicalMessage,
      visitsToClaimsMaxValue,
      visitsToClaimsSeries,
    }
  },
})

function formatNumber(value: number | string): string {
  const numericValue = typeof value === 'number' ? value : Number(value)
  return new Intl.NumberFormat().format(Number.isFinite(numericValue) ? numericValue : 0)
}

function formatPercent(value: number): string {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: safeValue >= 0.1 ? 0 : 1,
  }).format(safeValue)
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown time'
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function formatRelativeTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'just now'

  const diffMs = date.getTime() - Date.now()
  const diffMinutes = Math.round(diffMs / 60000)
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, 'minute')
  }

  const diffHours = Math.round(diffMinutes / 60)
  if (Math.abs(diffHours) < 48) {
    return formatter.format(diffHours, 'hour')
  }

  const diffDays = Math.round(diffHours / 24)
  return formatter.format(diffDays, 'day')
}
</script>

<style scoped>
.analytics-shell {
  width: min(94rem, calc(100% - 1rem));
  max-width: calc(100% - 1rem);
  margin: 5.75rem auto 2.75rem;
  display: grid;
  gap: 1rem;
  color: #ffffff;
  overflow-x: clip;
}

.analytics-header,
.surface,
.metrics-rail,
.section-nav,
.analytics-footer {
  border: 0.0625rem solid rgba(255, 255, 255, 0.12);
  background: var(--ui-inverse-surface);
  box-shadow: 0 1.2rem 2.5rem rgba(0, 0, 0, 0.18);
}

.analytics-header,
.section-nav,
.analytics-footer {
  border-radius: 1.1rem;
  padding: 1rem 1.15rem;
}

.surface,
.metrics-rail {
  border-radius: 1.25rem;
  padding: 1.2rem;
  min-width: 0;
}

.analytics-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.eyebrow {
  margin: 0 0 0.35rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
}

.header-copy h1 {
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  color: #ffffff;
}

.header-subtle {
  margin: 0.45rem 0 0;
  color: #ffffff;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.mode-switch,
.section-nav {
  display: grid;
  gap: 0.3rem;
  width: 100%;
}

.mode-switch {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: min(16rem, 100%);
}

.section-nav {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.mode-chip,
.section-chip,
.ghost-button,
.primary-button {
  border: none;
  cursor: pointer;
  transition: background-color 180ms ease, color 180ms ease, transform 180ms ease, border-color 180ms ease;
}

.mode-chip,
.section-chip {
  width: 100%;
  min-height: 1.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  padding: 0.15rem 0.55rem;
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 0.0625rem solid var(--vt-c-accent);
  font-size: 0.74rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.1;
  text-align: center;
}

.mode-chip.active,
.section-chip.active {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--color-background);
}

.ghost-button,
.primary-button {
  border-radius: 999px;
  padding: 0.65rem 0.95rem;
  font-weight: 600;
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 0.0625rem solid var(--vt-c-accent);
}

.ghost-button {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.ghost-button:hover,
.mode-chip:hover,
.section-chip:hover {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.ghost-button--small {
  padding: 0.45rem 0.8rem;
}

.primary-button {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.metrics-rail {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.85rem;
}

.metric-card {
  min-width: 0;
  display: grid;
  gap: 0.25rem;
  padding: 0.95rem 1rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.05);
}

.metric-label,
.metric-detail,
.panel-header p,
.muted-message,
.activity-row p,
.entity-card p,
.diagnostic-card p {
  color: #ffffff;
}

.metric-label {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.metric-value {
  color: #ffffff;
  font-size: 1.1rem;
  line-height: 1.25;
}

.metric-value--truncate {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-detail {
  font-size: 0.8rem;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.analytics-grid--overview {
  grid-template-columns: minmax(18rem, 0.84fr) minmax(0, 1.16fr);
  align-items: start;
}

.overview-stack {
  display: grid;
  gap: 1rem;
  align-content: start;
}

.overview-stack--main {
  min-width: 0;
}

.analytics-grid--advanced {
  grid-template-columns: 1fr;
}

.feature-card--full {
  min-height: 20rem;
}

.feature-card--compact {
  padding: 1rem;
}

.feature-card--compact .panel-header {
  margin-bottom: 0.75rem;
}

.feature-card--compact .panel-header h2 {
  font-size: 1.08rem;
}

.feature-card--compact .panel-header p,
.feature-card--compact .panel-meta,
.feature-card--compact .bar-copy,
.feature-card--compact .ratio-card small {
  font-size: 0.84rem;
}

.feature-card--compact .bar-row {
  gap: 0.3rem;
}

.feature-card--compact .bar-track {
  height: 0.58rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.panel-header > div {
  min-width: 0;
}

.panel-header--compact {
  margin-bottom: 0.75rem;
}

.panel-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.panel-header-meta {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
}

.panel-header h2,
.stack-panel h3,
.leaderboard-card h4 {
  margin: 0;
  color: #ffffff;
}

.panel-header p {
  margin: 0.35rem 0 0;
  overflow-wrap: anywhere;
}

.activity-panel-copy {
  display: grid;
  gap: 0.12rem;
}

.activity-panel-line {
  display: block;
}

.panel-meta {
  white-space: nowrap;
  color: #ffffff;
  font-size: 0.84rem;
}

.bar-stack,
.leaderboard-list,
.entity-list,
.diagnostics-list,
.diagnostics-column,
.activity-list {
  display: grid;
  gap: 0.75rem;
}

.activity-list--scrollable {
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(10 * 4.5rem);
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-top: 0.75rem;
}

.activity-list--scrollable .activity-row {
  grid-template-columns: 1fr auto;
  grid-auto-rows: auto;
  align-items: start;
  padding: 0.7rem 0.85rem;
}

.activity-list--scrollable .activity-row .activity-event {
  grid-column: 1;
  grid-row: 1;
}

.activity-list--scrollable .activity-row .activity-actor-type {
  grid-column: 2;
  grid-row: 1;
  justify-self: end;
  font-size: 0.8rem;
}

.activity-list--scrollable .activity-row .activity-ids {
  grid-column: 1 / -1;
  grid-row: 2;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0.25rem 0 0;
}

.activity-list--scrollable .activity-row time {
  grid-column: 1 / -1;
  grid-row: 3;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 0.15rem;
}

.activity-modal-card {
  max-width: min(720px, 92vw);
  width: 100%;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
}

.activity-modal-close {
  top: 0.75rem;
  right: 0.75rem;
  transform: none;
  color: var(--vt-c-accent);
}

.activity-modal-close:hover,
.activity-modal-close:focus-visible {
  background: rgba(var(--vt-c-accent-rgb), 0.14);
  border-color: rgba(var(--vt-c-accent-rgb), 0.3);
  color: var(--vt-c-accent);
}

.bar-row {
  display: grid;
  gap: 0.4rem;
}

.bar-copy {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: #ffffff;
}

.bar-track {
  width: 100%;
  height: 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(88, 215, 160, 0.75), rgba(160, 239, 205, 0.96));
}

.ratio-grid,
.dual-grid,
.leaderboard-grid,
.geo-layout {
  display: grid;
  gap: 1rem;
}

.ratio-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ratio-grid--stacked {
  grid-template-columns: 1fr;
  gap: 0.55rem;
}

.ratio-grid--compact {
  margin-bottom: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.ratio-card--traffic {
  position: relative;
  padding-right: 2.2rem;
}

.ratio-card,
.leaderboard-card,
.entity-card,
.diagnostic-card,
.stack-panel,
.geo-sidebar,
.detail-panel,
.export-card,
.density-card,
.warning-panel {
  border-radius: 0.95rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
}

.ratio-grid--stacked .ratio-card {
  padding: 0.75rem 0.85rem;
  gap: 0.12rem;
}

.detail-layout,
.export-grid,
.export-jobs,
.density-grid,
.signal-list,
.timeline-list,
.insight-list {
  display: grid;
  gap: 1rem;
}

.detail-layout {
  grid-template-columns: minmax(16rem, 24rem) minmax(0, 1fr);
}

.detail-layout--geo {
  grid-template-columns: minmax(0, 2fr) minmax(16rem, 1fr);
}

.detail-layout--wide {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.detail-layout--flush-bottom {
  margin-bottom: 1rem;
}

.entity-list--selectable {
  align-content: flex-start;
}

.entity-card--interactive {
  border: 0.0625rem solid transparent;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, background-color 0.18s ease;
}

.entity-card--interactive:hover {
  background: rgba(255, 255, 255, 0.05);
}

.entity-card--interactive.active {
  border-color: rgba(126, 227, 183, 0.22);
  background: rgba(126, 227, 183, 0.08);
}

.detail-panel__header,
.detail-stats,
.detail-metrics,
.signal-row,
.export-card {
  display: flex;
}

.detail-panel__header,
.signal-row,
.export-card {
  justify-content: space-between;
  gap: 1rem;
}

.detail-panel__header {
  align-items: flex-start;
  margin-bottom: 1rem;
}

.detail-panel__header h3,
.export-card strong,
.density-card strong,
.signal-row strong,
.timeline-copy strong {
  margin: 0;
  color: #ffffff;
}

.detail-panel__header p,
.timeline-copy p,
.signal-row p,
.export-card p {
  margin: 0.3rem 0 0;
  color: #ffffff;
}

.export-jobs {
  gap: 0.75rem;
}

.export-job-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.025);
}

.export-job-row strong,
.export-job-row p {
  margin: 0;
}

.detail-stats {
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
  color: #ffffff;
  font-size: 0.86rem;
}

.detail-metrics {
  flex-wrap: wrap;
  gap: 0.7rem;
  margin-bottom: 1rem;
}

.detail-metric {
  min-width: 7rem;
  border-radius: 0.85rem;
  padding: 0.55rem 0.7rem;
  background: rgba(255, 255, 255, 0.04);
  display: grid;
  gap: 0.2rem;
}

.detail-metric strong {
  color: #ffffff;
}

.detail-metric small {
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.insight-list {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 1rem;
}

.insight-chip {
  border-radius: 0.85rem;
  padding: 0.65rem 0.75rem;
  background: rgba(255, 255, 255, 0.035);
  display: grid;
  gap: 0.18rem;
}

.insight-chip strong {
  color: #ffffff;
}

.insight-chip small {
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.timeline-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: flex-start;
  padding-bottom: 0.85rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.timeline-dot {
  width: 0.6rem;
  height: 0.6rem;
  margin-top: 0.35rem;
  border-radius: 999px;
  background: rgba(126, 227, 183, 0.78);
  box-shadow: 0 0 0 0.2rem rgba(126, 227, 183, 0.12);
}

.signal-list {
  margin-bottom: 1rem;
}

.signal-row {
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.signal-row span {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
}

.signal-row--traffic {
  align-items: flex-start;
}

.traffic-value {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.traffic-dot {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 999px;
  box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.06);
}

.traffic-value .traffic-dot {
  position: static;
}

.traffic-dot--healthy {
  background: rgba(126, 227, 183, 0.95);
}

.traffic-dot--warning {
  background: rgba(250, 211, 108, 0.95);
}

.traffic-dot--critical {
  background: rgba(246, 115, 115, 0.95);
}

.ratio-card span,
.geo-stat span {
  display: block;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #ffffff;
}

.ratio-card strong,
.geo-stat strong {
  display: block;
  margin-top: 0.3rem;
  font-size: 1.1rem;
  color: #ffffff;
}

.ratio-card small {
  display: block;
  margin-top: 0.35rem;
  color: #ffffff;
}

.portfolio-table,
.events-table {
  display: grid;
}

.history-chart {
  display: grid;
  gap: 0.8rem;
}

.history-chart-row {
  display: grid;
  grid-template-columns: minmax(6rem, 7rem) minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.history-chart-row--double {
  align-items: flex-start;
}

.history-chart-stack {
  display: grid;
  gap: 0.55rem;
}

.history-mini-stat {
  display: grid;
  gap: 0.3rem;
}

.history-mini-stat label {
  color: rgba(188, 199, 208, 0.72);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-chart-track {
  width: 100%;
  height: 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.history-chart-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(88, 215, 160, 0.75), rgba(160, 239, 205, 0.96));
}

.history-chart-fill--muted {
  background: linear-gradient(90deg, rgba(123, 210, 246, 0.72), rgba(160, 205, 250, 0.92));
}

.leaderboard-score-label {
  opacity: 0.55;
  font-size: 0.72em;
  font-weight: 500;
  margin: 0 4px 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.leaderboard-score-label:first-child {
  margin-left: 0;
}

.portfolio-row,
.events-row,
.leaderboard-row,
.activity-row,
.entity-head,
.entity-meta,
.analytics-footer {
  display: flex;
  align-items: center;
}

.portfolio-row,
.events-row {
  display: grid;
  gap: 0.75rem;
  padding: 0.85rem 0;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.portfolio-row {
  grid-template-columns: minmax(12rem, 1.7fr) repeat(3, minmax(0, 0.8fr));
}

.events-row {
  grid-template-columns: minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(10rem, 1fr);
}

.portfolio-row--head,
.events-row--head {
  color: #ffffff;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.row-link {
  border: none;
  padding: 0;
  background: transparent;
  color: #ffffff;
  text-align: left;
  cursor: pointer;
}

.row-link-select-shell {
  min-width: 0;
}

.row-link-select {
  width: 100%;
  min-height: 2.5rem;
  border-radius: 0.8rem;
  border: 0.0625rem solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  padding: 0.6rem 0.85rem;
  font: inherit;
  text-align: left;
  box-shadow: inset 0 0 0 0.0625rem rgba(255, 255, 255, 0.03);
}

.row-link-select:focus {
  outline: 0.125rem solid rgba(var(--vt-c-accent-rgb), 0.38);
  outline-offset: 0.08rem;
}

.row-link-select option {
  color: var(--color-background);
}

.portfolio-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.6rem;
  padding: 0.24rem 0.7rem;
  border-radius: 999px;
  border: 0.0625rem solid transparent;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  background: #96ffc9;
  color: #181b2f;
}

.portfolio-status-badge--active {
  background: #22c55e;
  border-color: #4ade80;
  color: #062814;
}

.portfolio-status-badge--paused {
  background: #facc15;
  border-color: #fde047;
  color: #422006;
}

.portfolio-status-badge--ended {
  background: #ef4444;
  border-color: #f87171;
  color: #ffffff;
}

.portfolio-status-badge--scheduled {
  background: #38bdf8;
  border-color: #7dd3fc;
  color: #082f49;
}

.portfolio-status-badge--unscheduled {
  background: #94a3b8;
  border-color: #cbd5e1;
  color: #0f172a;
}

.activity-row,
.leaderboard-row,
.entity-head,
.entity-meta,
.analytics-footer {
  justify-content: space-between;
  gap: 0.75rem;
}

.activity-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  background: rgba(255, 255, 255, 0.05);
  border: 0.0625rem solid rgba(255, 255, 255, 0.08);
}

.activity-event {
  min-width: 0;
  text-align: left;
}

.activity-actor-type {
  display: inline-flex;
  align-items: baseline;
  color: rgba(188, 199, 208, 0.9);
  font-size: 0.92rem;
  text-align: left;
}

.activity-ids {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-line;
}

.activity-row strong,
.entity-head strong,
.leaderboard-row strong,
.diagnostic-card strong {
  color: #ffffff;
}

.activity-row p,
.entity-card p {
  margin: 0;
  text-align: left;
}

.activity-row time {
  white-space: nowrap;
  text-align: right;
  align-self: center;
}

.activity-row time {
  color: #ffffff;
  font-size: 0.84rem;
  text-align: right;
  white-space: nowrap;
}

.entity-meta {
  flex-wrap: wrap;
  margin-top: 0.8rem;
  color: #ffffff;
  font-size: 0.86rem;
}

.leaderboard-card header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0.85rem;
  color: #ffffff;
}

.leaderboard-grid--wide {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 1rem;
}

.leaderboard-row--detailed {
  padding-bottom: 0.55rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.leaderboard-table {
  grid-template-columns: minmax(8rem, 0.9fr) minmax(5rem, 0.5fr) minmax(7rem, 0.7fr) minmax(8rem, 0.9fr) minmax(7rem, 0.6fr) minmax(12rem, 1.2fr);
}

.diagnostic-card.info {
  border-left: 0.2rem solid rgba(123, 210, 246, 0.8);
}

.diagnostic-card.warning {
  border-left: 0.2rem solid rgba(250, 211, 108, 0.9);
}

.diagnostic-card.critical,
.warning-panel {
  border-left: 0.2rem solid rgba(246, 115, 115, 0.9);
}

.geo-layout {
  grid-template-columns: minmax(0, 2fr) minmax(16rem, 1fr);
}

.geo-map {
  width: 100%;
  min-height: 20rem;
  border-radius: 1rem;
  overflow: hidden;
}

.geo-map__bg {
  fill: rgba(255, 255, 255, 0.03);
}

.geo-map__grid line {
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 0.22;
}

.geo-map__point {
  fill: rgba(124, 236, 191, 0.75);
}

.geo-map__point--gps {
  fill: rgba(123, 210, 246, 0.78);
}

.geo-map__point--campaign {
  fill: rgba(255, 206, 122, 0.82);
}

.density-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.density-grid--sources {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.density-card {
  padding: 0.85rem 0.9rem;
}

.density-card span {
  display: block;
  color: #ffffff;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.export-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.export-card {
  align-items: flex-start;
}

.geo-sidebar {
  display: grid;
  align-content: flex-start;
  gap: 0.8rem;
}

.geo-activity-list {
  display: grid;
  gap: 0.55rem;
}

.geo-activity-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.55rem;
  border-bottom: 0.0625rem solid rgba(255, 255, 255, 0.06);
}

.geo-activity-row strong {
  color: #ffffff;
}

.addon-entry {
  padding-top: 1rem;
}

.addon-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.locked-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.loading-state,
.empty-state,
.locked-state,
.warning-state {
  text-align: left;
}

.analytics-footer {
  justify-content: space-between;
  color: #ffffff;
}

@media (max-width: 78rem) {
  .metrics-rail {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .analytics-grid,
  .ratio-grid,
  .dual-grid,
  .geo-layout,
  .detail-layout,
  .detail-layout--geo,
  .detail-layout--wide,
  .export-grid,
  .insight-list,
  .leaderboard-grid--wide {
    grid-template-columns: 1fr;
  }

  .ratio-grid--compact {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .density-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 62rem) {
  .portfolio-row,
  .events-row,
  .leaderboard-table,
  .history-chart-row {
    grid-template-columns: 1fr;
  }

  .portfolio-row--head,
  .events-row--head {
    display: none;
  }

  .portfolio-row span,
  .events-row span {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .portfolio-row {
    gap: 0.6rem;
    padding: 0.25rem 0 0;
    border-bottom: 0;
  }

  .row-link-select-shell {
    display: block;
    width: 100%;
    margin-bottom: 0.15rem;
  }

  .row-link-select {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 3rem;
    padding: 0.8rem 2.3rem 0.8rem 0.95rem;
    font-size: 0.95rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .portfolio-cell {
    width: 100%;
    align-items: center;
    padding: 0.7rem 0.85rem;
    border-radius: 0.8rem;
    background: rgba(255, 255, 255, 0.06);
    border: 0.0625rem solid rgba(255, 255, 255, 0.08);
    font-size: 0.9rem;
  }

  .portfolio-cell::before {
    content: attr(data-label);
    display: block;
    width: 100%;
    color: rgba(188, 199, 208, 0.78);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: center;
  }
}

@media (max-width: 50rem) {
  .analytics-shell {
    width: calc(100% - 1rem);
    max-width: calc(100% - 1rem);
    margin-top: 5.5rem;
  }

  .surface,
  .metrics-rail {
    padding: 1rem;
  }

  .feature-card .panel-header {
    justify-content: center;
  }

  .feature-card .panel-header > div {
    width: 100%;
    text-align: center;
  }

  .feature-card .panel-header h2,
  .feature-card .panel-header p {
    text-align: center;
  }

  .analytics-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .section-nav {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .metrics-rail {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .timeline-row {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .timeline-row time {
    grid-column: 2;
  }

  .activity-row {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }

  .activity-event {
    grid-column: 1;
  }

  .activity-actor-type {
    grid-column: 1;
    margin-top: -0.25rem;
  }

  .activity-ids {
    grid-column: 1;
  }

  .activity-row time {
    grid-column: 2;
    grid-row: 1 / span 3;
    align-self: start;
  }
}

@media (max-width: 34rem) {
  .activity-row {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }

  .activity-row time {
    order: -1;
  }

  .activity-event,
  .activity-actor-type,
  .activity-ids,
  .activity-row time {
    grid-column: 1;
  }

  .activity-actor-type {
    margin-top: -0.1rem;
  }

  .activity-row time {
    grid-row: auto;
    justify-self: start;
    align-self: start;
    text-align: left;
    font-size: 0.8rem;
  }

  .ratio-grid--compact {
    grid-template-columns: 1fr;
  }

  .mode-switch,
  .section-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .metrics-rail {
    grid-template-columns: 1fr;
  }

  .header-actions,
  .locked-actions,
  .analytics-footer {
    flex-direction: column;
    align-items: stretch;
  }
}

.analytics-charts {
  padding: 1.25rem 1.5rem 1.5rem;
}
.analytics-charts .panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.chart-window-picker {
  display: inline-flex;
  gap: 0.25rem;
  background: rgba(15, 23, 42, 0.4);
  padding: 0.25rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.chart-window-button {
  padding: 0.45rem 1rem;
  border: none;
  background: transparent;
  color: #cbd5e1;
  font-size: 0.85rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}
.chart-window-button:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}
.chart-window-button--active,
.chart-window-button--active:hover {
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 600;
}
.charts-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
}
@media (max-width: 1100px) {
  .charts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.chart-card {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 10px;
  padding: 1rem 1rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.chart-card h3 {
  margin: 0;
  font-size: 0.95rem;
  color: #ffffff;
  font-weight: 600;
}
.chart-canvas-wrap {
  height: 240px;
  position: relative;
}
.muted-message--warn {
  color: #fca5a5;
}
@media (max-width: 640px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
  .chart-canvas-wrap {
    height: 200px;
  }
}

.analytics-download {
  padding: 1.25rem 1.5rem;
}
.download-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}
.download-header p {
  color: rgba(255, 255, 255, 0.75);
  max-width: 52ch;
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  line-height: 1.5;
}
.download-cta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}
.download-button {
  padding: 0.7rem 1.3rem;
  border-radius: 8px;
  border: 2px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s, opacity 0.2s;
}
.download-button:hover:not(:disabled) {
  transform: translateY(-1px);
}
.download-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.download-meta {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
}
.download-error {
  margin-top: 0.9rem;
  padding: 0.6rem 0.9rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 6px;
  color: #fca5a5;
  font-size: 0.88rem;
}
.download-option {
  margin-top: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.88rem;
  cursor: pointer;
}
.download-option input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.paywall-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: calc(var(--base-layout-modal-z-index) + 95);
  padding: 1rem;
}
.paywall-card {
  background: #1e293b;
  color: #ffffff;
  padding: 1.75rem 1.6rem;
  border-radius: 12px;
  width: min(32rem, 100%);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.paywall-close {
  position: absolute;
  top: 0.6rem;
  right: 0.8rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
}
.paywall-close:hover {
  color: #ffffff;
}
.paywall-card h3 {
  margin: 0 0 0.7rem;
  font-size: 1.15rem;
}
.paywall-card p {
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.5;
  margin: 0.5rem 0;
}
.paywall-quantity {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 1rem 0 0.5rem;
  flex-wrap: wrap;
}
.paywall-quantity label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.88rem;
}
.paywall-quantity input {
  width: 5rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.25);
  color: #ffffff;
  font-size: 0.95rem;
}
.paywall-quantity span {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}
.paywall-actions {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
}
.paywall-actions--stacked {
  flex-direction: column;
  align-items: stretch;
}
.paywall-primary,
.paywall-secondary {
  padding: 0.6rem 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s, background 0.2s;
}
.paywall-primary {
  background: var(--vt-c-accent);
  color: var(--color-background);
  border: 2px solid var(--vt-c-accent);
}
.paywall-primary:hover:not(:disabled) {
  opacity: 0.9;
}
.paywall-primary:disabled,
.paywall-secondary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.paywall-secondary {
  background: transparent;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.paywall-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.analytics-type-insights {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.type-insights-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.type-insights-header h2 {
  margin: 0 0 0.25rem;
}
.type-insights-header p {
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
}
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
}
.type-badge[data-kind="quiz"] { background: rgba(99, 102, 241, 0.25); border-color: rgba(99, 102, 241, 0.4); }
.type-badge[data-kind="survey"] { background: rgba(16, 185, 129, 0.25); border-color: rgba(16, 185, 129, 0.4); }
.type-badge[data-kind="ar"] { background: rgba(236, 72, 153, 0.25); border-color: rgba(236, 72, 153, 0.4); }
.type-badge[data-kind="map"] { background: rgba(245, 158, 11, 0.25); border-color: rgba(245, 158, 11, 0.4); }

.type-tile {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.type-tile > summary {
  list-style: none;
  cursor: pointer;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  transition: background 0.15s;
}
.type-tile > summary::-webkit-details-marker {
  display: none;
}
.type-tile > summary:hover {
  background: rgba(255, 255, 255, 0.04);
}
.type-tile-title {
  font-weight: 600;
  color: #ffffff;
  font-size: 0.95rem;
}
.type-tile-hint {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}
.type-tile-body {
  padding: 0.25rem 1rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.type-tile-body .muted-message {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0.75rem 0 0.25rem;
}

.answer-summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  align-items: baseline;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.78);
  margin: 0.75rem 0 1rem;
}
.answer-summary-truncated {
  color: #fbbf24;
  font-size: 0.82rem;
}
.answer-summary-list {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}
.answer-summary-question {
  background: rgba(15, 23, 42, 0.4);
  border-radius: 6px;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.answer-summary-question header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
  flex-wrap: wrap;
}
.answer-summary-question-label {
  font-weight: 600;
  color: #ffffff;
  font-size: 0.9rem;
}
.answer-summary-question-meta {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
}
.answer-summary-bars {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.answer-summary-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(100px, 3fr) auto;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}
.answer-summary-bar-label {
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.answer-summary-bar-track {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
}
.answer-summary-bar-fill {
  background: var(--vt-c-accent);
  height: 100%;
  border-radius: 4px;
  transition: width 0.2s ease;
}
.answer-summary-bar-count {
  color: rgba(255, 255, 255, 0.75);
  font-variant-numeric: tabular-nums;
  min-width: 2.5ch;
  text-align: right;
}
.answer-summary-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}

.analytics-over-time {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.over-time-totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.metric-card--pending {
  opacity: 0.75;
  border-style: dashed;
}

.metric-card--pending .metric-value {
  color: var(--text-muted, #64748b);
}

.over-time-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.chart-card--pending {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.chart-card--pending h3 {
  margin: 0;
}

.chart-pending {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 1rem;
  color: var(--text-muted, #64748b);
  background: rgba(100, 116, 139, 0.05);
  border-radius: 0.5rem;
  text-align: center;
}

.chart-pending p {
  margin: 0;
}

.chart-pending-sub {
  font-size: 0.8rem;
  opacity: 0.8;
}

.chart-pending code {
  background: rgba(100, 116, 139, 0.15);
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
  font-size: 0.85em;
}

.advanced-charts-drawer {
  margin: 1rem 0;
  border: 1px solid rgba(100, 116, 139, 0.2);
  border-radius: 0.75rem;
  background: rgba(100, 116, 139, 0.03);
  overflow: hidden;
}

.advanced-charts-drawer > summary {
  cursor: pointer;
  padding: 0.85rem 1.25rem;
  font-weight: 600;
  color: var(--text-muted, #475569);
  user-select: none;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.advanced-charts-drawer > summary::-webkit-details-marker {
  display: none;
}

.advanced-charts-drawer > summary::before {
  content: '▸';
  display: inline-block;
  transition: transform 0.15s ease;
  font-size: 0.8em;
}

.advanced-charts-drawer[open] > summary::before {
  transform: rotate(90deg);
}

.advanced-charts-drawer > summary:hover {
  background: rgba(100, 116, 139, 0.08);
}

.advanced-charts-drawer > .analytics-charts {
  margin: 0;
  border-top: 1px solid rgba(100, 116, 139, 0.15);
  border-radius: 0;
  background: transparent;
}
</style>
